import { useState, useEffect, useRef, useLayoutEffect, useMemo, lazy, Suspense, forwardRef } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import sheepMascot from "@/assets/landing/sheep-mascot-teal.png";
import evaAvatar from "@/assets/eva-avatar.jpg";
import { useReveal, useActiveSection } from "@/hooks/useReveal";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useRotator, ROTATOR_SWIPE_CSS } from "@/components/landing/useRotator";
import { EvaPlayButton } from "@/components/landing/EvaPlayButton";
import { EvaVoiceTap } from "@/components/landing/EvaVoiceTap";
import { EvaVoicePicker } from "@/components/landing/EvaVoicePicker";
import { EvaAvatarEgg } from "@/components/landing/EvaAvatarEgg";
import { openCallMeNow } from "@/lib/openCallMeNow";
import { EvaSavingsModal } from "@/components/landing/EvaSavingsModal";
// Insider Codes are pure attribution — captured via /r/:code or ?ref= and
// applied automatically at checkout. Buyers never enter a code on the pricing
// page (it's not a discount), so the old PromoCodeInput is gone.
import { supabase } from "@/integrations/supabase/shared";
import { startCheckout as launchCheckout } from "@/lib/startCheckout";
import { OFFER } from "@/lib/offer";
import { SiteNav } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";

/* P6.4 — Lazy-load below-fold heavy section */
const VoiceDemo = lazy(() => import("@/components/landing/VoiceDemo").then(m => ({ default: m.VoiceDemo })));

// P34.7 — Module-level trigger so deeply-nested components (Hero, CTA, MobileBuyBar)
// can open the savings modal without prop-drilling. The Index root subscribes.
const SAVINGS_OPEN_EVENT = "eva:open-savings-modal";
export const openSavingsModal = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(SAVINGS_OPEN_EVENT));
  }
};

const EvaSavingsModalRoot = () => {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener(SAVINGS_OPEN_EVENT, onOpen as EventListener);
    return () => window.removeEventListener(SAVINGS_OPEN_EVENT, onOpen as EventListener);
  }, []);
  return <EvaSavingsModal open={open} onOpenChange={setOpen} />;
};

/* ============================================================
   ELECTRICSHEEP — Eva single-page site (v5 light cream theme)
   Ported from /tmp/ref/components into one self-contained page.
   ============================================================ */

const PALETTE = {
  bg: "#F8F4EC",
  bgAlt: "#F2EBDC",
  ink: "#1a1a1a",
  ink2: "#3a3530",
  muted: "#5a5249",
  amber: "#C4823A",
  amberSoft: "#E8B458",
  line: "rgba(0,0,0,0.08)",
  card: "#FFFFFF",
  dark: "#171513",
};

const FONT_BODY = "Barlow, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const FONT_DISPLAY = "'Instrument Serif', 'Cormorant Garamond', Georgia, serif";
const FONT_MONO = "'JetBrains Mono', ui-monospace, monospace";

/* P18 — Eva typewriter messages for hero status pill */
const EVA_MESSAGES = [
  "you deserve a vacation, you know. 🌴",
  "answering your inbox since 4am ☕",
  "those quotes? sent. sleep in. 😴",
  "your 2pm? rescheduled. handled. ✅",
  "47 days no day off. just saying. 💪",
  "remembered her birthday for you. 🎂",
  "invoice #4471 paid. no chasing. 💸",
  "go take that walk. i've got it. 🚶",
  "btw, i like the new headshot. 📸",
  "your weekend is yours. welcome. 🎉",
  "online · whatsapp, sms, slack 📲",
  "say less. i'll handle it. ✨",
];

// (P32.4 — CAPABILITY_LINES + CTA_LINES tickers retired in favor of EvaVoiceTap easter eggs.)

function EvaTypewriterPill({ messages = EVA_MESSAGES }: { messages?: string[] } = {}) {
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const [text, setText] = useState(messages[0]);

  useEffect(() => {
    if (reduceMotion) { setText(messages[0]); return; }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    let msgIdx = 0;
    let charIdx = 0;
    let mode: "type" | "hold" | "erase" | "gap" = "type";
    // Loop 3 fix: never render an empty pill on first paint. Start with the
    // first character already typed, then continue the typing animation as
    // before. Avoids the blank-capsule flash on mobile without losing the
    // signature live-typing motion.
    charIdx = 1;
    setText(messages[0].slice(0, 1));
    const tick = () => {
      if (cancelled) return;
      const msg = messages[msgIdx];
      if (mode === "type") {
        charIdx += 1;
        setText(msg.slice(0, charIdx));
        if (charIdx >= msg.length) { mode = "hold"; timer = setTimeout(tick, 2800); return; }
        timer = setTimeout(tick, 42 + Math.random() * 18);
      } else if (mode === "hold") {
        mode = "erase";
        timer = setTimeout(tick, 60);
      } else if (mode === "erase") {
        charIdx -= 1;
        setText(msg.slice(0, Math.max(0, charIdx)));
        if (charIdx <= 0) { mode = "gap"; timer = setTimeout(tick, 400); return; }
        timer = setTimeout(tick, 22);
      } else {
        msgIdx = (msgIdx + 1) % messages.length;
        charIdx = 0;
        mode = "type";
        timer = setTimeout(tick, 60);
      }
    };
    timer = setTimeout(tick, 600);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [reduceMotion, messages]);

  return (
    <span className="eva-pill-text">
      <span>{text}</span>
      {!reduceMotion && <span className="eva-pill-caret" aria-hidden>▍</span>}
    </span>
  );
};

/* P30 — Reusable white capsule that hosts an EvaTypewriterPill.
   Used in 3 places: hero, capability section header, final CTA. */
const EvaPill = ({ messages, align = "left" }: { messages?: string[]; align?: "left" | "center" }) => (
  <div
    className="hero-status-pill"
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 10,
      padding: "8px 14px",
      background: "rgba(255,255,255,0.55)",
      backdropFilter: "blur(8px)",
      WebkitBackdropFilter: "blur(8px)",
      border: `1px solid ${PALETTE.line}`,
      borderRadius: 999,
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
      fontFamily: FONT_BODY,
      fontSize: 13,
      color: PALETTE.ink2,
      marginBottom: align === "center" ? 18 : 20,
      // L26 — Reserve width/height so the typewriter can't wrap or
      // shrink the pill mid-stroke (CLS source on mobile).
      minWidth: "min(100%, 32ch)",
      minHeight: 36,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      ...(align === "center" ? { marginLeft: "auto", marginRight: "auto" } : {}),
    }}
  >
    <EvaTypewriterPill messages={messages} />
  </div>
);

/* Checkout — calls create-stripe-checkout, which resolves price IDs from
 * stripe_config and stamps client_reference_id with the active Insider Code
 * (captured by /r/:code or the ?ref= URL param). Custom tier → Calendly. */
async function startCheckout(opts: {
  plan: "solo" | "studio";
  cycle: "monthly" | "quarterly" | "semiannual";
}): Promise<{ url?: string; error?: string }> {
  return launchCheckout(opts);
}

/* ---------- Tiny inline icons ---------- */
const Icon = forwardRef<SVGSVGElement, { name: string; size?: number; color?: string }>(({ name, size = 18, color = "currentColor" }, ref) => {
  const p: Record<string, JSX.Element> = {
    arrowRight: <path d="M5 12h14M13 6l6 6-6 6" />,
    arrowUpRight: <path d="M7 17L17 7M8 7h9v9" />,
    check: <path d="M5 13l4 4L19 7" />,
    x: <path d="M6 6l12 12M18 6l-12 12" />,
    chevDown: <path d="M6 9l6 6 6-6" />,
    phone: <path d="M22 16.9v3a2 2 0 01-2.2 2 19.8 19.8 0 01-8.6-3.1 19.5 19.5 0 01-6-6A19.8 19.8 0 012.1 4.2 2 2 0 014.1 2h3a2 2 0 012 1.7c.1.9.3 1.8.6 2.7a2 2 0 01-.5 2L8 9.7a16 16 0 006 6l1.3-1.3a2 2 0 012-.5c.9.3 1.8.5 2.7.6a2 2 0 011.7 2z" />,
    mail: <g><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 7l9 6 9-6"/></g>,
    calendar: <g><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 10h18M8 3v4M16 3v4"/></g>,
    invoice: <g><path d="M6 2h9l5 5v13a2 2 0 01-2 2H6a2 2 0 01-2-2V4a2 2 0 012-2z"/><path d="M14 2v6h6M9 13h6M9 17h4"/></g>,
    mic: <g><rect x="9" y="3" width="6" height="12" rx="3"/><path d="M5 11a7 7 0 0014 0M12 19v3"/></g>,
    brain: <path d="M9 2a3 3 0 013 2 3 3 0 016 0v0a3 3 0 011 6 3 3 0 01-3 5 3 3 0 01-5 2 3 3 0 01-5-2 3 3 0 01-3-5 3 3 0 011-6 3 3 0 015-2z" />,
    shield: <path d="M12 3l8 4v5c0 5-3.5 8.5-8 9-4.5-.5-8-4-8-9V7l8-4z" />,
    users: <g><path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 00-3-3.9M16 3.1a4 4 0 010 7.8"/></g>,
    whatsapp: <path d="M12 2a10 10 0 00-8.5 15.2L2 22l4.9-1.5A10 10 0 1012 2zm4.9 14.4c-.2.5-1.2 1-1.6 1-.4 0-.9.2-3-.6-2.5-1-4.2-3.6-4.3-3.8-.1-.2-1-1.4-1-2.6s.7-1.8.9-2c.2-.3.5-.3.7-.3h.5c.2 0 .4 0 .6.5s.8 2 .9 2.1c.1.2.1.3 0 .5-.1.2-.2.3-.4.5-.1.2-.3.3-.4.5-.1.1-.3.3-.1.6s.8 1.3 1.7 2c1.1 1 2.1 1.3 2.3 1.4.2.1.4.1.5-.1.2-.2.6-.7.8-1 .1-.2.3-.2.5-.1.2.1 1.3.6 1.5.8.2.1.4.2.4.3.1.1.1.6-.1 1.1z" />,
    slack: <path d="M6 15a2 2 0 11-2-2h2v2zm1 0a2 2 0 014 0v5a2 2 0 01-4 0v-5zM9 6a2 2 0 112-2v2H9zm0 1a2 2 0 010 4H4a2 2 0 010-4h5zm9 2a2 2 0 112 2h-2V9zm-1 0a2 2 0 01-4 0V4a2 2 0 014 0v5zm-2 9a2 2 0 11-2 2v-2h2zm0-1a2 2 0 010-4h5a2 2 0 010 4h-5z" />,
    telegram: <path d="M22 3L2 11l5 2 2 6 3-3 5 4 5-17z" />,
    sparkle: <path d="M12 3l1.8 5.4L19 10l-5.2 1.6L12 17l-1.8-5.4L5 10l5.2-1.6L12 3z" />,
  };
  const filled = ["whatsapp","slack","telegram","sparkle"].includes(name);
  return (
    <svg ref={ref} width={size} height={size} viewBox="0 0 24 24" fill={filled ? color : "none"} stroke={filled ? "none" : color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      {p[name]}
    </svg>
  );
});
Icon.displayName = "Icon";

/* ---------- Nav ---------- */
/* ---------- Nav (shared site chrome) ---------- */
const NAV_SECTIONS = [
  { href: "#capability", label: "What she does" },
  { href: "#proof", label: "Implementation" },
  { href: "#pricing", label: "Pricing" },
  { href: "#faq", label: "FAQ" },
];

const Nav = () => {
  const active = useActiveSection(["capability", "proof", "pricing", "faq"]);
  return (
    <SiteNav
      product="software"
      sections={NAV_SECTIONS}
      activeSection={active}
      primary={{ label: "See pricing", href: "#pricing" }}
      callSource="nav"
      cobrainHint
    />
  );
};

/* ---------- Phone mockup ---------- */
/* P11.1 — Channel-authentic chrome.
   Each channel renders its own status bar, header, bubble style, and composer —
   so WhatsApp actually looks like WhatsApp, iMessage looks like iMessage, etc. */
type ChannelKey = "whatsapp" | "sms" | "telegram" | "slack";
const CHANNELS: Record<ChannelKey, {
  /* Phone status bar */
  statusBg: string; statusFg: string; statusVariant: "android" | "ios";
  /* Header */
  headerBg: string; headerFg: string; headerSub: string;
  /* Body wallpaper */
  bodyBg: string; bodyPattern?: string;
  /* Bubbles */
  outBg: string; outFg: string; inBg: string; inFg: string;
  bubbleRadius: number; bubbleStyle: "tail" | "ios" | "telegram" | "slack-rows";
  /* Composer */
  composerBg: string; composerInputBg: string; composerPlaceholder: string;
  /* Misc */
  accent: string;
}> = {
  whatsapp: {
    statusBg: "#075E54", statusFg: "#fff", statusVariant: "android",
    headerBg: "#075E54", headerFg: "#fff", headerSub: "rgba(255,255,255,0.78)",
    bodyBg: "#ECE5DD",
    bodyPattern:
      "radial-gradient(rgba(0,0,0,0.025) 1px, transparent 1px), radial-gradient(rgba(0,0,0,0.02) 1px, transparent 1px)",
    outBg: "#DCF8C6", outFg: "#111", inBg: "#FFFFFF", inFg: "#111",
    bubbleRadius: 9, bubbleStyle: "tail",
    composerBg: "#F0F0F0", composerInputBg: "#FFFFFF",
    composerPlaceholder: "Type a message",
    accent: "#34B7F1",
  },
  sms: {
    statusBg: "#FFFFFF", statusFg: "#000", statusVariant: "ios",
    headerBg: "#F8F8F8", headerFg: "#000", headerSub: "#8E8E93",
    bodyBg: "#FFFFFF",
    outBg: "#0A84FF", outFg: "#fff", inBg: "#E9E9EB", inFg: "#000",
    bubbleRadius: 18, bubbleStyle: "ios",
    composerBg: "#FFFFFF", composerInputBg: "#FFFFFF",
    composerPlaceholder: "iMessage",
    accent: "#0A84FF",
  },
  telegram: {
    statusBg: "#517DA2", statusFg: "#fff", statusVariant: "android",
    headerBg: "#517DA2", headerFg: "#fff", headerSub: "rgba(255,255,255,0.78)",
    bodyBg: "#D7DCE0",
    bodyPattern:
      "radial-gradient(rgba(255,255,255,0.5) 1px, transparent 1px)",
    outBg: "#EFFDDE", outFg: "#111", inBg: "#FFFFFF", inFg: "#111",
    bubbleRadius: 12, bubbleStyle: "telegram",
    composerBg: "#FFFFFF", composerInputBg: "#FFFFFF",
    composerPlaceholder: "Message",
    accent: "#5288c1",
  },
  slack: {
    statusBg: "#3F0E40", statusFg: "#fff", statusVariant: "android",
    headerBg: "#3F0E40", headerFg: "#fff", headerSub: "rgba(255,255,255,0.65)",
    bodyBg: "#FFFFFF",
    outBg: "#FFFFFF", outFg: "#1D1C1D", inBg: "#FFFFFF", inFg: "#1D1C1D",
    bubbleRadius: 4, bubbleStyle: "slack-rows",
    composerBg: "#FFFFFF", composerInputBg: "#FFFFFF",
    composerPlaceholder: "Message #eva",
    accent: "#1264A3",
  },
};
const MSGS = [
  { role: "you", text: "Reschedule the Nguyens — boiler call ran long." },
  { role: "eva", text: "Moved them to Thursday 10am — sent confirmation + calendar invite." },
  { role: "you", text: "Did we ever invoice them for the March service?" },
  { role: "eva", text: "Yes — $840, paid March 22. Want me to send the annual check-in now?" },
];

/* P5.1 + P10.4 — Industry-aware demo scripts (6 categories incl. SaaS + realty) */
type IndustryKey = "trades" | "dental" | "legal" | "studio" | "saas" | "realty";
const INDUSTRY_LABEL: Record<IndustryKey, string> = {
  trades: "Trades", dental: "Dental", legal: "Legal", studio: "Studio", saas: "SaaS", realty: "Realty",
};
const SCRIPTS_BY_INDUSTRY: Record<IndustryKey, {
  msgs: { role: "you" | "eva"; text: string }[];
  reasoning: string[];
  chips: { e: string; t: string }[];
}> = {
  trades: {
    msgs: [
      { role: "you", text: "Mrs. Patel's faucet is leaking again — can you fit her in?" },
      { role: "eva", text: "Tomorrow 2pm — confirmed with her, sent quote $180." },
      { role: "you", text: "Did Henderson ever pay the March invoice?" },
      { role: "eva", text: "Yes — $620, paid Mar 22. Want me to send their annual check-in?" },
    ],
    reasoning: ["checking calendar…", "→ Tue 2pm free", "drafting reply + quote…"],
    chips: [{ e: "📅", t: "booked" }, { e: "✉️", t: "quote sent" }, { e: "💸", t: "$180 queued" }],
  },
  dental: {
    msgs: [
      { role: "you", text: "Cancel my 3pm cleaning today — kid's sick." },
      { role: "eva", text: "Done. Rescheduled Thursday 10am, sent confirmation + recall slot." },
      { role: "you", text: "Has Ms. Chen rebooked her crown follow-up?" },
      { role: "eva", text: "Not yet — sent her a reminder this morning. I'll chase Friday if no reply." },
    ],
    reasoning: ["pulling Mrs. Chen's chart…", "→ last visit Mar 14", "drafting recall…"],
    chips: [{ e: "🦷", t: "rescheduled" }, { e: "✉️", t: "reminder sent" }, { e: "📋", t: "chart updated" }],
  },
  legal: {
    msgs: [
      { role: "you", text: "What's the status on the Henderson filing?" },
      { role: "eva", text: "Filed Mon. Opposing counsel responded Wed — draft reply waiting your review." },
      { role: "you", text: "Bill the call I had with them this morning." },
      { role: "eva", text: "Logged 0.4 hrs to Henderson · matter #2024-118 · added to April invoice." },
    ],
    reasoning: ["pulling matter file…", "→ #2024-118 active", "drafting reply…"],
    chips: [{ e: "⚖️", t: "matter updated" }, { e: "✉️", t: "draft ready" }, { e: "💼", t: "0.4 hrs billed" }],
  },
  studio: {
    msgs: [
      { role: "you", text: "Send Acme the invoice for last month." },
      { role: "eva", text: "Sent — $4,200, net-30. Acme has paid 4/4 on time historically." },
      { role: "you", text: "Did the Foster brand work get approved?" },
      { role: "eva", text: "Yes — Sarah signed off Tue. Want me to schedule the kickoff call?" },
    ],
    reasoning: ["pulling Acme history…", "→ 4/4 on-time", "generating PDF…"],
    chips: [{ e: "💸", t: "invoice sent" }, { e: "📄", t: "PDF attached" }, { e: "🤝", t: "client cc'd" }],
  },
  saas: {
    msgs: [
      { role: "you", text: "Draft the renewal email for Acme — $4.8k MRR, anniversary next Tue." },
      { role: "eva", text: "Drafted. Same terms, 12-mo renewal, paid annually w/ 5% discount. Reviewing?" },
      { role: "you", text: "Send it. And summarize last week's churn." },
      { role: "eva", text: "Sent. Churn: 2 logos, $640 MRR. Both <90d. Reasons logged in HubSpot." },
    ],
    reasoning: ["pulling Acme contract…", "→ MRR $4.8k", "drafting renewal…"],
    chips: [{ e: "📧", t: "renewal sent" }, { e: "📊", t: "churn logged" }, { e: "✅", t: "synced HubSpot" }],
  },
  realty: {
    msgs: [
      { role: "you", text: "Buyer wants to see 14 Maple Sat 4pm — can we?" },
      { role: "eva", text: "Booked. Sent confirmation + lockbox code, cc'd listing agent." },
      { role: "you", text: "Did the Patels send the pre-approval letter?" },
      { role: "eva", text: "Yes — uploaded to their file. Loan amount $640k, expires June 14." },
    ],
    reasoning: ["checking listing calendar…", "→ Sat 4pm open", "drafting confirmation…"],
    chips: [{ e: "🏠", t: "showing booked" }, { e: "📨", t: "agent cc'd" }, { e: "📄", t: "doc filed" }],
  },
};
const INDUSTRY_KEYS: IndustryKey[] = ["trades", "dental", "legal", "studio", "saas", "realty"];

/* ============================================================
   P38.1 — Phone sub-components hoisted to module scope.
   Previously these lived inside <Phone /> and were re-created on
   every render, which caused React to unmount + remount EVERY
   existing bubble on every state tick (`setShown`, `setReasoning`,
   `setTyping`, `setChips`). That re-fired the `msg-fade` animation
   on the entire chat history many times per Eva turn — the source
   of the "popping like crazy on every action" bug.
   These are now stable references, so React keeps existing bubbles
   mounted and only the newest bubble fades in.
   ============================================================ */

type PhoneCh = (typeof CHANNELS)[ChannelKey];

function PhoneReadTicks({ color }: { color: string }) { return (
  <svg width="14" height="9" viewBox="0 0 14 9" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M1 4.5l2.5 2.5L8 1"/><path d="M5 4.5L7.5 7 12 1"/></svg>
); }

function PhoneStatusBar({ ch }: { ch: PhoneCh }) {
  if (ch.statusVariant === "ios") {
    return (
      <div style={{ background: ch.statusBg, color: ch.statusFg, padding: "8px 22px 4px", display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 12, fontWeight: 600 }}>
        <span style={{ fontVariantNumeric: "tabular-nums" }}>9:41</span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5, opacity: 0.9 }}>
          <svg width="15" height="9" viewBox="0 0 15 9" fill={ch.statusFg}><rect x="0" y="6" width="2" height="3" rx="0.5"/><rect x="3.5" y="4" width="2" height="5" rx="0.5"/><rect x="7" y="2" width="2" height="7" rx="0.5"/><rect x="10.5" y="0" width="2" height="9" rx="0.5"/></svg>
          <span style={{ fontSize: 9.5, fontWeight: 600, letterSpacing: "0.02em" }}>5G</span>
          <svg width="22" height="10" viewBox="0 0 22 10" fill="none" stroke={ch.statusFg} strokeWidth="0.8"><rect x="0.5" y="0.5" width="18" height="9" rx="2"/><rect x="2" y="2" width="15" height="6" fill={ch.statusFg}/><rect x="19.5" y="3" width="1.5" height="4" rx="0.5" fill={ch.statusFg}/></svg>
        </span>
      </div>
    );
  }
  return (
    <div style={{ background: ch.statusBg, color: ch.statusFg, padding: "8px 14px 5px", display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 11.5, fontWeight: 500 }}>
      <span style={{ fontVariantNumeric: "tabular-nums" }}>9:41</span>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4, opacity: 0.9 }}>
        <svg width="11" height="8" viewBox="0 0 11 8" fill={ch.statusFg}><path d="M5.5 0C2.5 0 0 2.5 0 5.5l5.5 2 5.5-2C11 2.5 8.5 0 5.5 0z"/></svg>
        <svg width="14" height="8" viewBox="0 0 14 8" fill={ch.statusFg}><rect x="0" y="5" width="2" height="3"/><rect x="3" y="3" width="2" height="5"/><rect x="6" y="1.5" width="2" height="6.5"/><rect x="9" y="0" width="2" height="8"/></svg>
        <svg width="20" height="9" viewBox="0 0 20 9" fill="none" stroke={ch.statusFg} strokeWidth="0.7"><rect x="0.5" y="0.5" width="17" height="8" rx="1.5"/><rect x="2" y="2" width="13" height="5" fill={ch.statusFg}/><rect x="18" y="2.5" width="1.3" height="4" fill={ch.statusFg}/></svg>
      </span>
    </div>
  );
};

function PhoneHeader({ tab, ch, industry }: { tab: ChannelKey; ch: PhoneCh; industry: IndustryKey }) {
  if (tab === "sms") {
    return (
      <div style={{ background: ch.headerBg, padding: "8px 12px 10px", display: "grid", gridTemplateColumns: "auto 1fr auto", alignItems: "center", borderBottom: "0.5px solid rgba(0,0,0,0.1)" }}>
        <button aria-label="Back" style={{ background: "transparent", border: 0, color: ch.accent, padding: "4px 6px", display: "inline-flex", alignItems: "center", gap: 3, cursor: "default" }}>
          <svg width="11" height="18" viewBox="0 0 11 18" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M9 1L1 9l8 8"/></svg>
          <span style={{ fontSize: 11, background: "#FF3B30", borderRadius: 999, color: "#fff", padding: "1px 6px", fontWeight: 600, marginLeft: 2 }}>2</span>
        </button>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
          <div style={{ position: "relative", width: 28, height: 28, boxShadow: "0 0 0 0.5px rgba(0,0,0,0.1)", borderRadius: "50%", overflow: "hidden", flexShrink: 0 }}>
            <img src={evaAvatar} alt="" width={28} height={28} loading="lazy" decoding="async" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
            <EvaAvatarEgg />
          </div>
          <span style={{ fontSize: 11, color: ch.headerFg, fontWeight: 500 }}>Eva ›</span>
        </div>
        <button aria-label="Video call" style={{ background: "transparent", border: 0, padding: 4, color: ch.accent, cursor: "default" }}>
          <svg width="20" height="14" viewBox="0 0 20 14" fill="currentColor"><rect x="0" y="0" width="14" height="14" rx="3"/><path d="M14 4l6-3v12l-6-3z"/></svg>
        </button>
      </div>
    );
  }
  if (tab === "slack") {
    return (
      <div style={{ background: ch.headerBg, color: ch.headerFg, padding: "8px 12px 8px", display: "flex", flexDirection: "column", gap: 4 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 13, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}>
            Acme Inc
            <svg width="9" height="6" viewBox="0 0 9 6" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M1 1l3.5 4L8 1"/></svg>
          </span>
          <button aria-label="New message" style={{ background: "rgba(255,255,255,0.12)", border: 0, color: ch.headerFg, padding: 5, borderRadius: 6, cursor: "default" }}>
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M9 1l3 3-7 7H2v-3z"/></svg>
          </button>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 7, paddingTop: 4, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
          <span style={{ fontSize: 14, color: ch.headerFg, opacity: 0.6, fontWeight: 600 }}>#</span>
          <span style={{ fontSize: 13, fontWeight: 600, color: ch.headerFg }}>eva</span>
          <span style={{ fontSize: 10.5, color: ch.headerSub, marginLeft: 4 }}>· 1</span>
        </div>
      </div>
    );
  }
  return (
    <div style={{ background: ch.headerBg, padding: "10px 14px", display: "flex", alignItems: "center", gap: 10 }}>
      {tab === "whatsapp" && (
        <svg width="9" height="14" viewBox="0 0 9 14" fill="none" stroke={ch.headerFg} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M7 1L1 7l6 6"/></svg>
      )}
      <div style={{ position: "relative", width: 36, height: 36, borderRadius: "50%", overflow: "hidden", flexShrink: 0, boxShadow: "0 0 0 1.5px rgba(255,255,255,0.18)" }}>
        <img src={evaAvatar} alt="" width={36} height={36} loading="lazy" decoding="async" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
        <EvaAvatarEgg />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: ch.headerFg, fontSize: 14, fontWeight: 600, lineHeight: 1.2 }}>Eva</div>
        <div style={{ color: ch.headerSub, fontSize: 11, display: "flex", alignItems: "center", gap: 5, marginTop: 2 }}>
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#4ade80", boxShadow: "0 0 5px rgba(74,222,128,0.6)" }} />
          {tab === "telegram" ? "online" : "online"} · {INDUSTRY_LABEL[industry]}
        </div>
      </div>
      {tab === "whatsapp" && (
        <div style={{ display: "inline-flex", gap: 14, color: ch.headerFg, opacity: 0.95 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.9v3a2 2 0 01-2.2 2 19.8 19.8 0 01-8.6-3.1 19.5 19.5 0 01-6-6A19.8 19.8 0 012.1 4.2 2 2 0 014.1 2h3a2 2 0 012 1.7c.1.9.3 1.8.6 2.7a2 2 0 01-.5 2L8 9.7a16 16 0 006 6l1.3-1.3a2 2 0 012-.5c.9.3 1.8.5 2.7.6a2 2 0 011.7 2z"/></svg>
        </div>
      )}
      {tab === "telegram" && (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={ch.headerFg} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.9v3a2 2 0 01-2.2 2 19.8 19.8 0 01-8.6-3.1 19.5 19.5 0 01-6-6A19.8 19.8 0 012.1 4.2 2 2 0 014.1 2h3a2 2 0 012 1.7c.1.9.3 1.8.6 2.7a2 2 0 01-.5 2L8 9.7a16 16 0 006 6l1.3-1.3a2 2 0 012-.5c.9.3 1.8.5 2.7.6a2 2 0 011.7 2z"/></svg>
      )}
    </div>
  );
};

function PhoneBubble({ role, text, isLast, ch }: { role: "you" | "eva"; text: string; isLast: boolean; ch: PhoneCh }) {
  const out = role === "you";
  if (ch.bubbleStyle === "slack-rows") {
    const isApp = role === "eva";
    return (
      <div style={{ display: "flex", gap: 8, padding: "4px 4px", alignItems: "flex-start", animation: "msg-fade 0.32s ease-out both", position: "relative" }}>
        {isApp ? (
          <div style={{ position: "relative", width: 26, height: 26, flexShrink: 0, borderRadius: 4, overflow: "hidden", background: "#4A154B" }}>
            <img src={evaAvatar} alt="" width={26} height={26} loading="lazy" decoding="async" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
            <EvaAvatarEgg />
          </div>
        ) : (
          <div style={{ width: 26, height: 26, borderRadius: 4, overflow: "hidden", flexShrink: 0, background: "#4A154B" }}>
            <div style={{ width: "100%", height: "100%", display: "grid", placeItems: "center", color: "#fff", fontSize: 11, fontWeight: 700, fontFamily: FONT_BODY }}>You</div>
          </div>
        )}
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 2 }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: "#1D1C1D" }}>{isApp ? "Eva" : "You"}</span>
            {isApp && <span style={{ fontSize: 8.5, fontWeight: 700, padding: "1px 4px", borderRadius: 3, background: "#E8E8E8", color: "#616061", letterSpacing: "0.04em" }}>APP</span>}
            <span style={{ fontSize: 10, color: "#616061" }}>9:4{isLast ? "1" : "0"} AM</span>
          </div>
          <div style={{ fontSize: 12.5, color: "#1D1C1D", lineHeight: 1.4 }}>{text}</div>
        </div>
      </div>
    );
  }
  if (ch.bubbleStyle === "ios") {
    return (
      <div style={{ alignSelf: out ? "flex-end" : "flex-start", maxWidth: "78%", animation: "msg-fade 0.32s ease-out both", position: "relative" }}>
        <div style={{
          background: out ? ch.outBg : ch.inBg,
          color: out ? ch.outFg : ch.inFg,
          padding: "7px 12px",
          borderRadius: ch.bubbleRadius,
          fontSize: 13.5, lineHeight: 1.35,
        }}>
          {text}
        </div>
        {isLast && out && (
          <div style={{ fontSize: 9.5, color: "#8E8E93", textAlign: "right", marginTop: 2, marginRight: 2, letterSpacing: "0.02em" }}>Delivered</div>
        )}
      </div>
    );
  }
  if (ch.bubbleStyle === "telegram") {
    return (
      <div style={{ alignSelf: out ? "flex-end" : "flex-start", maxWidth: "78%", animation: "msg-fade 0.32s ease-out both", position: "relative" }}>
        <div style={{
          background: out ? ch.outBg : ch.inBg,
          color: out ? ch.outFg : ch.inFg,
          padding: "6px 10px 5px",
          borderRadius: ch.bubbleRadius,
          fontSize: 13, lineHeight: 1.35,
          boxShadow: "0 1px 1px rgba(0,0,0,0.08)",
          paddingRight: out ? 38 : 10,
        }}>
          {text}
          <span style={{ position: "absolute", right: 8, bottom: 5, display: "inline-flex", alignItems: "center", gap: 3, fontSize: 9.5, color: out ? "#4FAE4E" : "rgba(0,0,0,0.45)" }}>
            {out && <>9:41 <PhoneReadTicks color="#4FAE4E" /></>}
          </span>
        </div>
      </div>
    );
  }
  // WhatsApp tail bubble
  return (
    <div style={{ alignSelf: out ? "flex-end" : "flex-start", maxWidth: "78%", animation: "msg-fade 0.32s ease-out both", position: "relative" }}>
      <div style={{
        background: out ? ch.outBg : ch.inBg,
        color: out ? ch.outFg : ch.inFg,
        padding: "5px 9px 17px",
        borderRadius: ch.bubbleRadius,
        fontSize: 13, lineHeight: 1.35,
        boxShadow: "0 1px 0.5px rgba(0,0,0,0.13)",
        position: "relative",
      }}>
        {text}
        <span style={{ position: "absolute", right: 7, bottom: 3, display: "inline-flex", alignItems: "center", gap: 3, fontSize: 9.5, color: "rgba(0,0,0,0.45)" }}>
          9:41
          {out && <PhoneReadTicks color="#53BDEB" />}
        </span>
      </div>
    </div>
  );
};

function PhoneComposer({ tab, ch }: { tab: ChannelKey; ch: PhoneCh }) {
  if (tab === "slack") {
    return (
      <div style={{ padding: "8px 10px", borderTop: "1px solid rgba(0,0,0,0.08)", background: "#fff" }}>
        <div style={{ border: "1px solid #DDDDDE", borderRadius: 8, padding: "6px 8px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, paddingBottom: 5, borderBottom: "1px solid #F2F2F2", marginBottom: 5 }}>
            <span style={{ fontFamily: "Georgia, serif", fontSize: 11, fontWeight: 700, color: "#616061" }}>B</span>
            <span style={{ fontFamily: "Georgia, serif", fontSize: 11, fontWeight: 700, color: "#616061", fontStyle: "italic" }}>I</span>
            <span style={{ fontSize: 11, color: "#616061", textDecoration: "line-through" }}>S</span>
            <span style={{ fontSize: 11, color: "#616061" }}>{"</>"}</span>
            <span style={{ flex: 1 }} />
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#616061" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
          </div>
          <div style={{ fontSize: 12, color: "#999" }}>{ch.composerPlaceholder}</div>
        </div>
      </div>
    );
  }
  if (tab === "sms") {
    return (
      <div style={{ padding: "6px 8px 8px", display: "flex", alignItems: "center", gap: 6, background: "#FFF", borderTop: "0.5px solid rgba(0,0,0,0.1)" }}>
        <button aria-label="Apps" style={{ width: 26, height: 26, borderRadius: "50%", background: "#E5E5EA", border: 0, color: "#8E8E93", display: "grid", placeItems: "center", cursor: "default" }}>
          <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 1v10M1 6h10"/></svg>
        </button>
        <div style={{ flex: 1, padding: "5px 12px", background: "#fff", borderRadius: 999, border: "1px solid #D1D1D6", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 12, color: "#8E8E93" }}>{ch.composerPlaceholder}</span>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#8E8E93" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="3" width="6" height="12" rx="3"/><path d="M5 11a7 7 0 0014 0M12 19v3"/></svg>
        </div>
      </div>
    );
  }
  if (tab === "telegram") {
    return (
      <div style={{ padding: "8px 10px", display: "flex", alignItems: "center", gap: 8, background: "#fff", borderTop: "1px solid rgba(0,0,0,0.06)" }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7d8e9a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
        <div style={{ flex: 1, fontSize: 12.5, color: "#999" }}>{ch.composerPlaceholder}</div>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7d8e9a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.4 11.05l-9.95 9.95a5.66 5.66 0 01-8-8L13.4 3.05a3.77 3.77 0 015.33 5.33L8.77 18.34a1.88 1.88 0 11-2.66-2.66L15.05 6.85"/></svg>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#5288c1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="3" width="6" height="12" rx="3"/><path d="M5 11a7 7 0 0014 0"/></svg>
      </div>
    );
  }
  // WhatsApp
  return (
    <div style={{ padding: "7px 8px", display: "flex", alignItems: "center", gap: 6, background: "transparent" }}>
      <div style={{ flex: 1, padding: "7px 12px", background: ch.composerInputBg, borderRadius: 999, fontSize: 12.5, color: "#8E8E93", display: "flex", alignItems: "center", gap: 8 }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8E8E93" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2M9 9h.01M15 9h.01"/></svg>
        <span style={{ flex: 1 }}>{ch.composerPlaceholder}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8E8E93" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.4 11.05l-9.95 9.95a5.66 5.66 0 01-8-8L13.4 3.05a3.77 3.77 0 015.33 5.33L8.77 18.34a1.88 1.88 0 11-2.66-2.66L15.05 6.85"/></svg>
      </div>
      <button aria-label="Voice" style={{ width: 32, height: 32, borderRadius: "50%", background: "#075E54", border: 0, color: "#fff", display: "grid", placeItems: "center", cursor: "default", flexShrink: 0 }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="3" width="6" height="12" rx="3"/><path d="M5 11a7 7 0 0014 0M12 19v3"/></svg>
      </button>
    </div>
  );
};

function Phone() {
  const [tab, setTab] = useState<ChannelKey>("whatsapp");
  const [industry, setIndustry] = useState<IndustryKey>("trades");
  const [pinned, setPinned] = useState(false);
  const [shown, setShown] = useState(0);
  const [typing, setTyping] = useState(false);
  const [reasoning, setReasoning] = useState(0);
  const [chips, setChips] = useState(false);
  const ch = CHANNELS[tab];
  const script = SCRIPTS_BY_INDUSTRY[industry];
  // P38.5 — stable msgs reference per industry (defensive against future dep changes).
  const msgs = useMemo(() => script.msgs, [industry]);

  // Auto-rotate industry every 24s unless user pinned one (P36.4)
  useEffect(() => {
    if (pinned) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const t = window.setInterval(() => {
      setIndustry((cur) => INDUSTRY_KEYS[(INDUSTRY_KEYS.indexOf(cur) + 1) % INDUSTRY_KEYS.length]);
    }, 24000);
    return () => clearInterval(t);
  }, [pinned]);

  useEffect(() => {
    setShown(0); setTyping(false); setReasoning(0); setChips(false);
    const timers: number[] = [];
    const run = (i: number) => {
      if (i >= msgs.length) {
        timers.push(window.setTimeout(() => setChips(true), 200));
        timers.push(window.setTimeout(() => { setChips(false); setShown(0); setReasoning(0); run(0); }, 4500));
        return;
      }
      const isEva = msgs[i].role === "eva";
      if (isEva) {
        timers.push(window.setTimeout(() => setReasoning(1), 250));
        timers.push(window.setTimeout(() => setReasoning(2), 700));
        timers.push(window.setTimeout(() => setReasoning(3), 1150));
        timers.push(window.setTimeout(() => setTyping(true), 1500));
        timers.push(window.setTimeout(() => {
          setTyping(false); setReasoning(0); setShown(i + 1);
          timers.push(window.setTimeout(() => run(i + 1), 1400));
        }, 2100));
      } else {
        timers.push(window.setTimeout(() => {
          setShown(i + 1);
          timers.push(window.setTimeout(() => run(i + 1), 900));
        }, 500));
      }
    };
    timers.push(window.setTimeout(() => run(0), 400));
    return () => timers.forEach(clearTimeout);
    // P38.2 — `msgs` intentionally NOT in deps: it's derived from `industry`
    // and including it risks re-firing the timeline on reconciliation hiccups,
    // which would reset the chat from scratch and look like a "pop".
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, industry]);

  const tabs: { id: ChannelKey; label: string; icon: string }[] = [
    { id: "whatsapp", label: "WhatsApp", icon: "whatsapp" },
    { id: "sms",      label: "iMessage", icon: "phone" },
    { id: "telegram", label: "Telegram", icon: "telegram" },
    { id: "slack",    label: "Slack",    icon: "slack" },
  ];


  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
      <EvaVoicePicker />
      <div data-channel-tabs role="tablist" aria-label="Choose a messaging channel" style={{ background: "#fff", borderRadius: 999, padding: 4, display: "inline-flex", gap: 2, boxShadow: "0 1px 3px rgba(0,0,0,0.05), 0 0 0 1px rgba(0,0,0,0.04)", maxWidth: "94vw", overflowX: "auto", scrollSnapType: "x mandatory" }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} className="channel-tab-btn"
            role="tab"
            aria-selected={tab === t.id}
            aria-controls="phone-conversation"
            id={`phone-tab-${t.id}`}
            tabIndex={tab === t.id ? 0 : -1}
            style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "8px 12px", borderRadius: 999, border: 0,
            background: tab === t.id ? PALETTE.ink : "transparent",
            color: tab === t.id ? "#fff" : PALETTE.muted,
            fontSize: 12, fontWeight: 500, cursor: "pointer", transition: "all 180ms",
            fontFamily: FONT_BODY, scrollSnapAlign: "start", flexShrink: 0,
          }}>
            <Icon name={t.icon} size={13} /> <span className="channel-tab-label">{t.label}</span>
          </button>
        ))}
        <style>{`
          [data-channel-tabs]::-webkit-scrollbar{display:none}
          [data-channel-tabs]{scrollbar-width:none}
          /* P13.5 — On narrow phones, switch to a 4-up grid so all four channels are visible without scroll. */
          @media (max-width: 460px) {
            [data-channel-tabs] {
              display: grid !important;
              grid-template-columns: repeat(4, 1fr) !important;
              max-width: calc(100vw - 32px) !important;
              overflow: visible !important;
              gap: 0 !important;
            }
            [data-channel-tabs] .channel-tab-btn { padding: 8px 6px !important; font-size: 11px !important; gap: 4px !important; justify-content: center; }
          }
        `}</style>
      </div>
      <div style={{ position: "relative" }}>
        {/* P29.8 / Loop41 — Tiny play button anchored to the actual phone frame so it can't drift off-screen on mobile. */}
        <div className="hero-phone-play" style={{ position: "absolute", top: -10, right: -10, zIndex: 4 }}>
          <EvaPlayButton clipId="hero" size="sm" label="Hear from Eva" />
        </div>
        <div data-phone-frame style={{
          width: "min(320px, 86vw)", aspectRatio: "320 / 600",
          borderRadius: 44,
          background: "#1a1a1a", padding: 10,
          boxShadow: "0 30px 60px -20px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.06)",
          // P38.6 — whisper-calm float (was 8s / -3px). After bubble remount fix,
          // any extra motion is more noticeable, so we tone this way down.
          animation: "phone-float 10s ease-in-out infinite",
        }}>
        <div style={{ width: "100%", height: "100%", borderRadius: 36, overflow: "hidden", background: ch.bodyBg, display: "flex", flexDirection: "column", position: "relative" }}>
          <PhoneStatusBar ch={ch} />
          <PhoneHeader tab={tab} ch={ch} industry={industry} />
          <div
            role="tabpanel"
            id="phone-conversation"
            aria-labelledby={`phone-tab-${tab}`}
            style={{
            flex: 1,
            padding: tab === "slack" ? "8px 6px" : "12px 10px",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            gap: tab === "slack" ? 2 : 6,
            // P38.3 — bottom-anchor the chat so new bubbles flow into the
            // empty space below the date divider instead of pushing earlier
            // bubbles upward (which read as a "pop" / layout shift).
            justifyContent: "flex-end",
            minHeight: 0,
            background: ch.bodyBg,
            backgroundImage: ch.bodyPattern,
            backgroundSize: ch.bodyPattern ? "16px 16px, 24px 24px" : undefined,
            backgroundPosition: ch.bodyPattern ? "0 0, 8px 8px" : undefined,
            position: "relative",
          }}>
            {/* Date divider */}
            {tab !== "slack" && (
              <div style={{ alignSelf: "center", padding: "2px 10px", borderRadius: 999, background: tab === "sms" ? "transparent" : "rgba(255,255,255,0.85)", color: "rgba(0,0,0,0.5)", fontSize: 10, marginBottom: 4, fontWeight: 500, letterSpacing: "0.02em" }}>
                {tab === "sms" ? "Today 9:41 AM" : "TODAY"}
              </div>
            )}
            {msgs.slice(0, shown).map((m, i) => (
              <PhoneBubble key={i} role={m.role as "you" | "eva"} text={m.text} isLast={i === shown - 1} ch={ch} />
            ))}
            {reasoning > 0 && (
              <div style={{
                alignSelf: tab === "slack" ? "stretch" : "flex-start",
                background: "rgba(196,130,58,0.08)",
                border: "1px solid rgba(196,130,58,0.25)",
                borderRadius: 12,
                padding: "8px 10px",
                maxWidth: tab === "slack" ? "100%" : "82%",
                fontFamily: FONT_MONO,
                fontSize: 11,
                color: "#5a4a2c",
                lineHeight: 1.45,
                animation: "msg-fade 0.32s ease-out both",
                marginLeft: tab === "slack" ? 38 : 0,
              }}>
                <div style={{ fontSize: 9, letterSpacing: "0.14em", textTransform: "uppercase", color: PALETTE.amber, fontWeight: 700, marginBottom: 4 }}>
                  Eva is thinking
                </div>
                {script.reasoning.slice(0, reasoning).map((line, i) => (
                  <div key={i} style={{ animation: "msg-fade 0.28s ease-out both" }}>{line}</div>
                ))}
              </div>
            )}
            {typing && (
              <div style={{ alignSelf: tab === "slack" ? "stretch" : "flex-start", marginLeft: tab === "slack" ? 38 : 0, background: ch.inBg, padding: "7px 12px", borderRadius: ch.bubbleStyle === "ios" ? 18 : 12, fontSize: 14, lineHeight: 1, color: ch.inFg, display: "inline-flex", alignItems: "center", gap: 3, width: "fit-content", animation: "msg-fade 0.28s ease-out both" }}>
                <Dot delay={0} /><Dot delay={0.15} /><Dot delay={0.3} />
              </div>
            )}
            {/* P38.4 — chip row reserves its slot from the start (min-height)
                so chips appearing/disappearing never shoves the bubble stack. */}
            <div style={{ minHeight: 28, display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4, marginLeft: tab === "slack" ? 38 : 0 }}>
              {chips && script.chips.map((c, i) => (
                <span key={i} style={{
                  display: "inline-flex", alignItems: "center", gap: 5,
                  padding: "5px 9px", borderRadius: 999,
                  background: "rgba(196,130,58,0.14)", color: "#5a4a2c",
                  fontSize: 10.5, fontWeight: 600, letterSpacing: "0.02em",
                  animation: `chip-pop 0.34s ease-out both`,
                  animationDelay: `${i * 110}ms`,
                }}>
                  <span aria-hidden style={{ fontSize: 11 }}>{c.e}</span>{c.t}
                </span>
              ))}
            </div>
          </div>
          <PhoneComposer tab={tab} ch={ch} />
        </div>
      </div>
      </div>
    </div>
  );
};

const Dot = forwardRef<HTMLSpanElement, { delay?: number }>(({ delay = 0 }, ref) => (
  <span ref={ref} style={{ width: 6, height: 6, borderRadius: "50%", background: "#999", animation: `dot 1.2s ease-in-out ${delay}s infinite` }} />
));
Dot.displayName = "Dot";

/* P5.5 — Hero ambient drift particles */
const HeroParticles = () => {
  const particles = [
    { top: "18%", left: "62%", size: 7, anim: "drift-1", dur: "14s", delay: "0s",   pulseDur: "3.2s" },
    { top: "32%", left: "88%", size: 5, anim: "drift-2", dur: "17s", delay: "1.2s", pulseDur: "4.1s" },
    { top: "58%", left: "70%", size: 6, anim: "drift-3", dur: "13s", delay: "0.4s", pulseDur: "3.6s" },
    { top: "72%", left: "92%", size: 4, anim: "drift-4", dur: "15s", delay: "2.0s", pulseDur: "2.9s" },
    { top: "12%", left: "78%", size: 5, anim: "drift-5", dur: "16s", delay: "0.8s", pulseDur: "3.4s" },
    { top: "48%", left: "54%", size: 4, anim: "drift-6", dur: "18s", delay: "1.6s", pulseDur: "4.5s" },
  ];
  return (
    <div aria-hidden style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 0, overflow: "hidden" }}>
      {particles.map((p, i) => (
        <span
          key={i}
          data-hero-particle
          style={{
            top: p.top, left: p.left,
            width: p.size, height: p.size,
            animation: `${p.anim} ${p.dur} ease-in-out ${p.delay} infinite, particle-pulse ${p.pulseDur} ease-in-out infinite`,
          }}
        />
      ))}
    </div>
  );
};

/* P5.5 — Magnetic button wrapper (desktop pointer only) */
const Magnetic = ({ children, strength = 0.18 }: { children: React.ReactNode; strength?: number }) => {
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const fine = window.matchMedia("(pointer: fine)").matches;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!fine || reduce) return;
    const onMove = (e: MouseEvent) => {
      const r = el.getBoundingClientRect();
      const dx = e.clientX - (r.left + r.width / 2);
      const dy = e.clientY - (r.top + r.height / 2);
      el.style.transform = `translate(${dx * strength}px, ${dy * strength}px)`;
    };
    const onLeave = () => { el.style.transform = "translate(0,0)"; };
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
    };
  }, [strength]);
  return <span ref={ref} data-magnetic>{children}</span>;
};

/* ---------- Hero ---------- */
function Hero() {
  const phoneRef = useReveal<HTMLDivElement>({ threshold: 0.05 });
  const statsRef = useReveal<HTMLDivElement>();
  const showHeroVideo = useMediaQuery("(min-width: 641px) and (prefers-reduced-motion: no-preference)");
  return (
  <section id="top" aria-label="Hero" style={{ padding: "clamp(100px, 14vw, 120px) 20px 56px", background: PALETTE.bg, position: "relative", overflow: "hidden" }}>
    {/* P9.A40 — Skip-link target. The <a href="#main-content"> in index.html
        needs a real anchor; mount it as an invisible block at the very top of the hero. */}
    <span id="main-content" tabIndex={-1} aria-hidden style={{ position: "absolute", top: 0, left: 0, width: 1, height: 1 }} />
    {showHeroVideo && (
      <>
        <video
          aria-hidden="true"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          src="https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260324_151826_c7218672-6e92-402c-9e45-f1e0f454bdc4.mp4"
          style={{
            position: "absolute", inset: 0, width: "100%", height: "100%",
            objectFit: "cover", zIndex: 0, opacity: 0.38,
            mixBlendMode: "luminosity", pointerEvents: "none",
          }}
        />
        <div aria-hidden style={{
          position: "absolute", inset: 0, zIndex: 0, pointerEvents: "none",
          background: "linear-gradient(180deg, #F8F4EC 0%, rgba(248,244,236,0.55) 35%, rgba(248,244,236,0.75) 100%)",
        }} />
      </>
    )}
    <HeroParticles />
    {/* Soft amber spotlight behind the phone */}
    <div aria-hidden style={{
      position: "absolute", top: "-10%", right: "-15%", width: "70%", height: "120%",
      background: `radial-gradient(circle at 60% 40%, ${PALETTE.amberSoft}33 0%, ${PALETTE.amber}14 28%, transparent 60%)`,
      filter: "blur(40px)", pointerEvents: "none", zIndex: 0,
    }} />
    <div style={{ position: "relative", zIndex: 1, maxWidth: 1200, margin: "0 auto", display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", gap: 64, alignItems: "center" }} className="hero-grid">
      <div style={{ maxWidth: 580, minWidth: 0 }}>
        <p style={{ fontSize: 13, color: PALETTE.amber, fontWeight: 600 }}>HireEva · an Electric Sheep offering</p>
        <EvaPill />
        <h1 style={{ fontFamily: FONT_DISPLAY, fontStyle: "italic", fontWeight: 400, fontSize: "clamp(40px, 6.2vw, 92px)", lineHeight: 1.02, letterSpacing: "-0.02em", margin: 0, color: PALETTE.ink, textWrap: "balance", maxWidth: 660 }}>
          Your AI chief of staff, deployed by an engineer.
        </h1>
        <p style={{ marginTop: 28, fontSize: 18, lineHeight: 1.55, color: PALETTE.ink2, maxWidth: "min(520px, 100%)" }}>
          An Electric Sheep engineer implements Eva and the AI workflows around her in your business: client replies, invoicing, scheduling, and connected tools. We scope the work with you, deploy it, and provide ongoing support.
        </p>
        <div className="hero-cta-row" style={{ display: "flex", gap: 12, marginTop: 24, flexWrap: "wrap", position: "relative", zIndex: 41 }}>
          {/* P22.1 — Magnetic wrapper removed: it caused a translucent hover artifact on this CTA. */}
          <button
            type="button"
            onClick={() => openCallMeNow("hero")}
            className="hero-cta-primary"
            style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "14px 22px", background: PALETTE.ink, color: "#fff", borderRadius: 999, border: 0, cursor: "pointer", fontSize: 14, fontWeight: 500, fontFamily: "inherit", transition: "background 200ms ease, transform 200ms ease" }}
          >
            Scope your Eva deployment <Icon name="arrowRight" size={15} />
          </button>
          <button
              type="button"
              onClick={() => openCallMeNow("hero")}
              className="hero-cta-secondary"
              style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "14px 22px", background: "transparent", border: "1px solid rgba(0,0,0,0.18)", color: PALETTE.ink2, borderRadius: 999, cursor: "pointer", fontFamily: "inherit", fontSize: 14, fontWeight: 500 }}
            >
              <Icon name="phone" size={14} /> Talk to Us Now
            </button>
        </div>
        <p style={{ fontSize: 14, lineHeight: 1.6, marginTop: 20 }}>
          Need a broader integration project? <a href="https://www.electricsheephq.com/contact?intent=implementation" style={{ color: PALETTE.ink, textDecoration: "underline" }}>Talk to Electric Sheep →</a>
        </p>
        {/* P13.8 — On mobile the ticker now sits BELOW the CTAs (above the phone via DOM order in the parent grid) */}
      </div>
      <div ref={phoneRef} data-reveal className="hero-phone-wrap" style={{ justifySelf: "end", position: "relative", zIndex: 41 }}>
        <Phone />
      </div>
    </div>

    {/* P27.2 — Outcome promise tiles replace the neutral channel/setup/promise/support strip */}
    <div ref={statsRef} data-reveal-stagger data-mobile-2col className="hero-stats" style={{ position: "relative", zIndex: 41, maxWidth: 1200, margin: "64px auto 0", padding: "32px 0", borderTop: `1px solid ${PALETTE.line}`, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 28 }}>
        {[
          { icon: "sparkle", l: "Your own engineer", b: "Embedded with your team. You don't touch code." },
          { icon: "mic", l: "Trained on your voice", b: "She replies how you would. Approve before send." },
          { icon: "mail", l: "Every channel you use", b: "WhatsApp · SMS · email · Slack · voice." },
          { icon: "users", l: "Ongoing support", b: "An engineering team maintains your deployment." },
        ].map((s, i) => (
          <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
            <div aria-hidden style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(196,130,58,0.12)", color: PALETTE.amber, display: "grid", placeItems: "center", flexShrink: 0 }}>
              <Icon name={s.icon} size={16} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: PALETTE.ink, lineHeight: 1.3 }}>{s.l}</div>
              <div style={{ fontSize: 12.5, color: PALETTE.muted, marginTop: 4, lineHeight: 1.45 }}>{s.b}</div>
            </div>
          </div>
        ))}
    </div>

    <style>{`
      @keyframes phone-float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-2px)} }
      @keyframes led { 50% { opacity: 0.4 } }
      @keyframes msg-pop { from { opacity: 0; transform: translateY(6px) } to { opacity: 1; transform: none } }
      @keyframes msg-fade { from { opacity: 0 } to { opacity: 1 } }
      @keyframes dot { 0%,80%,100% { transform: scale(0.6); opacity: 0.5 } 40% { transform: scale(1); opacity: 1 } }
      /* P22.1 — clean hover on hero primary CTA (Magnetic wrapper removed) */
      .hero-cta-primary:hover { background: #2a2a2a !important; transform: translateY(-1px); }
      .hero-cta-secondary:hover { background: rgba(0,0,0,0.04) !important; transform: translateY(-1px); }
      /* P9.A36 — center the phone column explicitly. The previous :last-child selector
         actually matched .hero-ticker-below-phone (the third grid child), leaving the
         phone wrapper pinned to the right via its inline justifySelf:end. */
      @media (max-width: 980px){
        .hero-grid { grid-template-columns: 1fr !important; }
        .hero-phone-wrap {
          justify-self: center !important;
          width: 100% !important;
          display: flex !important;
          justify-content: center !important;
          align-items: center !important;
        }
        .hero-ticker-below-phone { justify-self: center !important; }
      }
      @media (max-width: 640px){
        .hero-stats { grid-template-columns: repeat(2, 1fr) !important; gap: 20px !important; margin-top: 32px !important; padding: 16px 0 !important; }
        .hero-stats > div { text-align: center !important; }
        .hero-phone-play { display: none !important; }
        .hero-ticker-desktop { display: none !important; }
        .hero-ticker-mobile-inline { display: none !important; }
        .hero-ticker-below-phone { display: flex !important; justify-content: center !important; margin-top: 0 !important; }
        .hero-phone-wrap { margin-bottom: -16px !important; }
        .hero-status-pill {
          white-space: nowrap !important;
          overflow: hidden !important;
          text-overflow: ellipsis !important;
          max-width: calc(100vw - 40px) !important;
          min-width: 0 !important;
          line-height: 1.2;
          margin-left: auto !important; margin-right: auto !important;
          display: inline-flex !important;
        }
        .hero-status-pill .eva-pill-text { min-width: 0; overflow: hidden; text-overflow: ellipsis; }
        /* L19 · P19.1 + P6.A25 — Hero text column fully centered on mobile, pill ABOVE headline */
        .hero-grid > div:first-child {
          max-width: 100% !important; text-align: center !important;
          display: flex !important; flex-direction: column !important; align-items: center !important;
          position: relative;
        }
        /* P6.A25 — soft amber radial haze behind hero on mobile (no new image) */
        .hero-grid > div:first-child::before {
          content: ""; position: absolute; inset: -40px -20px auto -20px; height: 320px;
          background: radial-gradient(ellipse at 50% 30%, rgba(196,130,58,0.10) 0%, rgba(196,130,58,0) 65%);
          pointer-events: none; z-index: 0;
        }
        .hero-grid > div:first-child > * { position: relative; z-index: 1; }
        .hero-grid > div:first-child > .hero-status-pill { order: 1; margin-top: 0 !important; margin-bottom: 16px !important; }
        .hero-grid > div:first-child > h1 {
          order: 2; text-align: center !important; margin-left: auto !important; margin-right: auto !important; margin-top: 8px !important;
          font-size: clamp(40px, 11vw, 56px) !important; line-height: 1.02 !important;
        }
        .hero-grid > div:first-child > p {
          order: 3; text-align: center !important; margin-left: auto !important; margin-right: auto !important;
          max-width: 52ch !important;
        }
        .hero-grid > div:first-child > .hero-cta-row { order: 4; }
        .hero-grid > div:first-child > .hero-proof-row { order: 5; justify-content: center !important; text-align: center; }
        .hero-grid > div:first-child > .hero-ticker-mobile-inline { order: 6; }
        /* P13.3 — grid-1col is the only reliable way to make children full-width here */
        .hero-cta-row { display: grid !important; grid-template-columns: 1fr !important; gap: 10px !important; width: 100% !important; }
        .hero-cta-row [data-magnetic] { display: block !important; width: 100% !important; }
        .hero-cta-row .hero-cta-primary,
        .hero-cta-row .hero-cta-secondary {
          display: flex !important; width: 100% !important; box-sizing: border-box !important;
          padding: 16px 20px !important;
        }
        /* P13.4 — tighter phone with real side margin */
        [data-phone-frame] { width: min(280px, 72vw) !important; aspect-ratio: 300 / 540 !important; border-radius: 32px !important; }
        [data-channel-tabs] {
          -webkit-mask-image: linear-gradient(to right, black 88%, transparent);
          mask-image: linear-gradient(to right, black 88%, transparent);
          animation: tabs-peek 900ms ease-out 600ms 1 both;
        }
        /* L16 · P16.2 — Center SectionHead block on mobile (Capability/Compare/Proof/Pricing/FAQ) */
        [data-section-head-wrap] { margin-bottom: 32px !important; }
        [data-section-head] { flex-direction: column !important; align-items: center !important; gap: 8px !important; text-align: center !important; margin-bottom: 14px !important; }
        [data-section-h2] { text-align: center !important; }
        [data-section-lede] { text-align: center !important; margin-left: auto !important; margin-right: auto !important; }
        /* L16 · P16.1 — Capability cards: tighter, hide EVA eyebrow, prefix Eva line with ↳ */
        [data-cap-grid] { gap: 12px !important; }
        [data-cap-card] { padding: 20px !important; }
        /* P22.6 — eyebrow + divider removed; Eva is now an inline amber lead word in the same paragraph. */
        [data-eva-line] { margin-top: 10px !important; }
        /* L19 · P19.4 + L25.5 — Capability cards left-align on mobile so the icon stays attached to the title and body copy reads naturally. */
        [data-cap-card] { text-align: left !important; }
        [data-cap-head] { justify-content: flex-start !important; }
        [data-cap-card] p { text-align: left !important; }
        /* L16 · P16.3 — Proof stats grid: center numbers in their cells on mobile */
        [data-mobile-2col] > div { text-align: center !important; }
        /* L16 · P16.5 — "How she gets started" steps center-stack on mobile */
        [data-start-strip] > div { justify-content: center !important; text-align: center !important; }
        [data-start-strip] > div:first-child { text-align: center !important; }
        [data-start-strip] > div:not(:first-child) { flex-direction: column !important; align-items: center !important; gap: 6px !important; }
        /* L16 · P16.6 — Proof testimonial cards: center stars + author row on mobile */
        [data-quote-stars] { justify-content: center !important; }
        [data-quote-author] { justify-content: center !important; text-align: center !important; }
        [data-quote-author] > div:last-child > div:first-child { justify-content: center !important; }
      }
      /* P13.5 — At ≤460 the tabs become a 4-up grid and don't need mask/peek */
      @media (max-width: 460px) {
        [data-channel-tabs] { -webkit-mask-image: none !important; mask-image: none !important; animation: none !important; }
      }
      @keyframes tabs-peek { 0% { transform: translateX(0) } 35% { transform: translateX(-12px) } 100% { transform: translateX(0) } }
      @media (prefers-reduced-motion: reduce) {
        [data-phone-frame] { animation: none !important; }
        [data-channel-tabs] { animation: none !important; }
      }
    `}</style>
  </section>
  );
};

/* ---------- Section header (numbered eyebrow) ---------- */
/* Loop 3 — Brand-aware eyebrow renderer.
   Keeps proper casing for brand words ("Eva", "ChatGPT", "Claude", "NVIDIA",
   "evaOS", "ElectricSheep") even when the surrounding container uses
   text-transform: uppercase. Honors the project-wide casing rule. */
const BRAND_WORDS = ["evaOS", "ElectricSheep", "ChatGPT", "Claude", "NVIDIA", "Eva"];
const renderBrandEyebrow = (text: string): React.ReactNode => {
  // Match brand words with word boundaries, case-insensitive.
  const pattern = new RegExp(`\\b(${BRAND_WORDS.join("|")})\\b`, "gi");
  const parts: React.ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = pattern.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const matched = m[0].toLowerCase();
    const canonical = BRAND_WORDS.find(b => b.toLowerCase() === matched) || m[0];
    parts.push(
      <span key={`b${key++}`} style={{ textTransform: "none" }}>{canonical}</span>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length ? parts : text;
};

const SectionHead = ({ n, eyebrow, title, italic, lede, dark = false, ledeTrailing }: { n: string; eyebrow: string; title: React.ReactNode; italic?: string; lede?: string; dark?: boolean; ledeTrailing?: React.ReactNode }) => {
  const ink = dark ? "#fff" : PALETTE.ink;
  const mute = dark ? "rgba(255,255,255,0.55)" : PALETTE.muted;
  const ink2 = dark ? "rgba(255,255,255,0.7)" : PALETTE.ink2;
  return (
    <div data-section-head-wrap style={{ marginBottom: 56 }}>
      <div data-section-head style={{ display: "flex", alignItems: "baseline", gap: 24, marginBottom: 18 }}>
        <span style={{ fontFamily: FONT_DISPLAY, fontStyle: "italic", fontSize: 32, color: PALETTE.amber, lineHeight: 1 }}>{n}</span>
        <span style={{ fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: mute, fontWeight: 500 }}>{renderBrandEyebrow(eyebrow)}</span>
      </div>
      <h2 data-section-h2 style={{ fontFamily: FONT_DISPLAY, fontWeight: 400, fontSize: "clamp(36px, 5vw, 60px)", lineHeight: 1.05, letterSpacing: "-0.015em", margin: 0, color: ink, textWrap: "balance" }}>
        {title} {italic && <em style={{ color: PALETTE.amber }}>{italic}</em>}
      </h2>
      {lede && (
        <p data-section-lede style={{ marginTop: 24, fontSize: 17, lineHeight: 1.55, color: ink2, maxWidth: 720 }}>
          {lede}{ledeTrailing}
        </p>
      )}
    </div>
  );
};

/* ---------- Capability (now also hosts the 3-step "how she gets started" footer · P10.1) ---------- */
const Capability = () => {
  const caps = [
    { icon: "mail", title: "Inbox that never empties", body: "Quotes, reschedules, 'quick questions' at 11pm.", eva: "Eva answers in your voice — drafts or sends, your rule.", clipId: "faq_voice" as const, hint: "won't she sound robotic?" },
    { icon: "calendar", title: "Calendar that lies", body: "Double-bookings. No-shows. Jobs swallowed by next week.", eva: "Eva holds the calendar. Books, confirms, reschedules." },
    { icon: "invoice", title: "Invoices you chase", body: "Half-drafted in notes. Half sent are 30+ days late.", eva: "Eva sends from your templates and chases the late ones." },
    { icon: "brain", title: "Head full of tabs", body: "Every client's name, their dog's name, what you promised.", eva: "Eva remembers every client, every conversation. She picks up where you left off — an hour or a month later.", clipId: "faq_memory" as const, hint: "does she really remember?" },
    { icon: "mic", title: "Voice notes", body: "Dictate on site, mid-drive, at 11pm.", eva: "Eva turns voice notes into invoices, follow-ups, calendar blocks." },
    { icon: "users", title: "Works with your team", body: "Approval rules per teammate, per client.", eva: "Eva follows your rules — what she sends, and when." },
  ];
  const startSteps = [
    { n: "01", t: "You tell her your business.", b: "One 45-min onboarding call." },
    { n: "02", t: "We plug her in.", b: "Inbox, calendar, invoicing, CRM, messaging." },
    { n: "03", t: "We validate and support it.", b: "Rollout timing is agreed for your scope." },
  ];
  const gridRef = useReveal<HTMLDivElement>();
  return (
    <section id="capability" data-capability-rail style={{ padding: "clamp(40px, 8vw, 72px) clamp(20px, 5vw, 24px) clamp(44px, 10vw, 88px)", background: PALETTE.bgAlt }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <SectionHead n="01" eyebrow="You hired her to run your business" title="You didn't start a business to" italic="live in your inbox." lede="We implement connected workflows around the routine work that takes time from your team. Scope and approval rules are agreed with your engineer." ledeTrailing={<EvaVoiceTap clipId="faq_different" hint="is this just chatgpt?" />} />
        <div ref={gridRef} data-reveal-stagger data-cap-grid style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
          {caps.map((c, i) => (
            <div key={i} data-cap-card style={{ background: PALETTE.card, borderRadius: 20, padding: 28, border: `1px solid ${PALETTE.line}` }}>
              <div data-cap-head style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                <div data-cap-icon style={{ width: 36, height: 36, borderRadius: 8, background: "rgba(196,130,58,0.12)", color: PALETTE.amber, display: "grid", placeItems: "center", flexShrink: 0 }}>
                  <Icon name={c.icon} size={18} />
                </div>
                <h3 style={{ fontFamily: FONT_BODY, fontSize: 17, fontWeight: 600, color: PALETTE.ink, margin: 0, lineHeight: 1.25, flex: 1, minWidth: 0 }}>{c.title}</h3>
              </div>
              <p style={{ fontSize: 14, color: PALETTE.muted, lineHeight: 1.5, margin: 0 }}>{c.body}</p>
              {/* P22.6 — drop "EVA" eyebrow; inline amber "Eva" lead word saves ~24px per card */}
              <p data-eva-line style={{ marginTop: 12, fontSize: 14, color: PALETTE.ink2, lineHeight: 1.5, marginBottom: 0 }}>
                <span style={{ color: PALETTE.amber, fontWeight: 600 }}>Eva</span>
                {c.eva.replace(/^Eva/, "")}
                {("clipId" in c) && c.clipId ? (
                  <EvaVoiceTap clipId={c.clipId} hint={("hint" in c) ? c.hint : undefined} />
                ) : null}
              </p>
            </div>
          ))}
        </div>
        {/* P10.1 — Compact "how she gets started" strip, replaces the standalone HowItWorks section */}
        <div data-start-strip style={{
          marginTop: 40, padding: "22px 24px",
          background: PALETTE.card,
          border: `1px solid ${PALETTE.line}`,
          borderRadius: 16,
          display: "grid", gridTemplateColumns: "auto repeat(3, 1fr)", gap: 24, alignItems: "center",
        }}>
          <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: PALETTE.amber, fontWeight: 700, lineHeight: 1.3 }}>
            How she<br/>gets started
          </div>
          {startSteps.map((s, i) => (
            <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
              <span style={{ fontFamily: FONT_DISPLAY, fontStyle: "italic", fontSize: 28, color: PALETTE.amber, lineHeight: 1, flexShrink: 0 }}>{s.n}</span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: PALETTE.ink, lineHeight: 1.3 }}>{s.t}</div>
                <div style={{ fontSize: 12.5, color: PALETTE.muted, marginTop: 4, lineHeight: 1.4 }}>{s.b}</div>
              </div>
            </div>
          ))}
          <style>{`
            @media (max-width: 760px) {
              [data-start-strip] { grid-template-columns: 1fr !important; gap: 16px !important; padding: 18px 18px !important; }
            }
          `}</style>
        </div>
      </div>
    </section>
  );
};

/* HowItWorks merged into Capability footer · P10.1 */

/* P31.12 — The Stack band: "Eva built herself" — authority, hardware, integrations */
const StackBand = () => {
  const logos: Array<{ name: string; icon: string }> = [
    { name: "WhatsApp", icon: "whatsapp" },
    { name: "Gmail", icon: "mail" },
    { name: "Slack", icon: "slack" },
    { name: "QuickBooks", icon: "invoice" },
    { name: "HubSpot", icon: "users" },
    { name: "Stripe", icon: "sparkle" },
  ];
  const cards = [
    {
      icon: "sparkle",
      title: "Engineer-led implementation",
      body: "Electric Sheep connects Eva to your tools and workflows, with ongoing maintenance and support.",
    },
    {
      icon: "shield",
      title: "Dedicated hosting",
      body: "Dedicated hosting we provide and maintain for your deployment. Connected services and AI providers still process the requests sent to them.",
    },
    {
      icon: "mic",
      title: "Built-in voice stack",
      body: "Voice capabilities are configured for your deployment. We agree the service providers and data flow as part of implementation.",
    },
  ];
  return (
    <section style={{ background: PALETTE.bgAlt, padding: "clamp(56px, 8vw, 80px) 24px" }}>
      <div style={{ maxWidth: 1080, margin: "0 auto", textAlign: "center" }}>
        <div style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: PALETTE.amber, fontWeight: 700, marginBottom: 16 }}>
          The stack · who built Eva
        </div>
        <h2 style={{ fontFamily: FONT_DISPLAY, fontStyle: "italic", fontWeight: 400, fontSize: "clamp(26px, 3.4vw, 36px)", lineHeight: 1.15, letterSpacing: "-0.015em", margin: 0, color: PALETTE.ink }}>
          Built and supported by Electric Sheep.
        </h2>
        <p style={{ marginTop: 16, fontSize: 15, lineHeight: 1.65, color: PALETTE.ink2, maxWidth: 720, margin: "16px auto 0" }}>
          HireEva is Electric Sheep's Eva-led business AI offering. Our engineers implement the workflows, integrations, and approval rules your team needs, then maintain the deployment.
        </p>
        <p style={{ marginTop: 12, fontSize: 15, lineHeight: 1.65, color: PALETTE.ink2, maxWidth: 720, margin: "12px auto 0" }}>
          Dedicated hosting gives your deployment its own runtime resources. It does not mean requests stay entirely on that server: AI model providers and connected tools process data needed for their services. We scope those connections with you.
        </p>

        {/* P31.12 — 3 authority cards */}
        <div style={{
          marginTop: 36,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          gap: 16,
          textAlign: "left",
        }}>
          {cards.map((c, i) => (
            <div key={i} style={{
              padding: "20px 22px",
              background: PALETTE.card,
              border: `1px solid ${PALETTE.line}`,
              borderRadius: 14,
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}>
              <div aria-hidden style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(196,130,58,0.12)", color: PALETTE.amber, display: "grid", placeItems: "center" }}>
                <Icon name={c.icon} size={16} />
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: PALETTE.ink, lineHeight: 1.3 }}>{c.title}</div>
              <div style={{ fontSize: 13, color: PALETTE.muted, lineHeight: 1.55 }}>{c.body}</div>
            </div>
          ))}
        </div>

        {/* P31.12 — supporting integration row, reduced visual weight */}
        <div style={{ marginTop: 28, fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: PALETTE.muted, fontWeight: 600, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
          Integrations scoped to your workflow
        </div>
        <div style={{
          marginTop: 14, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, flexWrap: "wrap",
        }}>
          {logos.map((l, i) => (
            <div key={i} style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "6px 12px",
              background: "transparent",
              border: `1px solid ${PALETTE.line}`,
              borderRadius: 999,
              fontSize: 12, color: PALETTE.ink2, fontWeight: 500,
            }}>
              <span style={{ color: PALETTE.amber, display: "inline-flex" }}><Icon name={l.icon} size={12} /></span>
              {l.name}
            </div>
          ))}
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "6px 12px",
            background: "rgba(196,130,58,0.08)",
            border: "1px solid rgba(196,130,58,0.22)",
            borderRadius: 999,
            fontSize: 11, color: PALETTE.amber, fontWeight: 700,
            letterSpacing: "0.04em",
          }}>
            Custom integrations
          </div>
        </div>
      </div>
    </section>
  );
};

/* P10.8 — Inline conversion CTA strip between long sections */
const InlineCTA = ({ href, eyebrow, line, cta }: { href: string; eyebrow: string; line: string; cta: string }) => (
  <div style={{ background: PALETTE.bg, padding: "32px 24px 32px" }}>
    <a
      href={href}
      data-inline-cta
      style={{
        display: "flex", alignItems: "center", gap: 18,
        maxWidth: 980, margin: "0 auto",
        padding: "16px 22px",
        background: "rgba(196,130,58,0.10)",
        border: "1px solid rgba(196,130,58,0.25)",
        borderRadius: 999,
        textDecoration: "none",
        color: PALETTE.ink,
        boxShadow: "0 1px 0 rgba(0,0,0,0.04)",
        transition: "background 200ms, transform 200ms",
      }}
    >
      <span style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: PALETTE.amber, fontWeight: 700, flexShrink: 0 }}>{eyebrow}</span>
      <span style={{ flex: 1, fontSize: 14.5, color: PALETTE.ink2, lineHeight: 1.4 }}>{line}</span>
      <span style={{ fontSize: 13, fontWeight: 600, color: PALETTE.amber, whiteSpace: "nowrap" }}>{cta}</span>
      <style>{`
        [data-inline-cta]:hover { background: rgba(196,130,58,0.14) !important; transform: translateY(-1px); }
        @media (max-width: 640px) {
          [data-inline-cta] { flex-wrap: wrap !important; gap: 8px !important; padding: 14px 18px !important; border-radius: 16px !important; }
          [data-inline-cta] > span:nth-child(1) { font-size: 9px !important; }
          [data-inline-cta] > span:nth-child(2) { flex: 1 1 100% !important; order: 3; font-size: 13px !important; }
          [data-inline-cta] > span:nth-child(3) { width: 100% !important; text-align: left !important; order: 4; margin-left: 0 !important; font-size: 12.5px !important; }
        }
      `}</style>
    </a>
  </div>
);

/* ---------- Compare (DARK section) ---------- */
function Compare() {
  const rows = [
    { l: "Answers clients in your voice", eva: true, human: true, gpt: "Sort of", diy: false },
    { l: "Remembers every client + promise", eva: true, human: "Sort of", gpt: false, diy: "Sort of" },
    { l: "Works on WhatsApp, SMS, voice", eva: true, human: true, gpt: false, diy: false },
    { l: "Sends + chases invoices", eva: true, human: true, gpt: false, diy: false },
    { l: "Available 24/7, nights + weekends", eva: true, human: false, gpt: true, diy: true },
    { l: "Setup time", eva: "24 hrs", human: "2–4 wks", gpt: "you build it", diy: "forever" },
    { l: "Monthly cost", eva: "From $1,500 incl. engineer", human: "$4k+", gpt: "$20 + prompts", diy: "your time" },
    { l: "You fix it when it breaks", eva: false, human: false, gpt: true, diy: true },
  ];
  const cell = (v: boolean | string) => {
    if (v === true)  return <Icon name="check" size={18} color={PALETTE.amberSoft} />;
    if (v === false) return <Icon name="x"     size={18} color="rgba(255,255,255,0.25)" />;
    return <span style={{ color: "rgba(255,255,255,0.55)", fontSize: 13 }}>{v}</span>;
  };
  const tableRef = useReveal<HTMLDivElement>();
  return (
    <section id="compare" style={{ padding: "clamp(44px, 10vw, 88px) clamp(20px, 5vw, 24px)", background: PALETTE.dark, color: "#fff" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <SectionHead n="02" eyebrow="Eva vs. the alternatives" title="You could do this another way." italic="Here's why people don't." dark />
        <div ref={tableRef} data-reveal data-compare-grid style={{ borderRadius: 14, overflow: "hidden", border: "1px solid rgba(255,255,255,0.08)" }}>
          <div data-compare-header style={{ display: "grid", gridTemplateColumns: "minmax(160px, 1.4fr) repeat(4, minmax(110px, 1fr))", background: "rgba(255,255,255,0.03)" }}>
            <div />
            {[
              { l: "Eva", hi: true },
              { l: "Human assistant" },
              { l: "ChatGPT / Claude" },
              { l: "Do it yourself" },
            ].map((h, i) => (
              <div key={i} {...(h.hi ? { "data-eva-header": true } : {})} style={{ padding: "20px 18px", background: h.hi ? "rgba(196,130,58,0.18)" : "transparent", fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: h.hi ? PALETTE.amberSoft : "rgba(255,255,255,0.55)", fontWeight: 600 }}>{renderBrandEyebrow(h.l)}</div>
            ))}
          </div>
          {rows.map((r, i) => (
            <div key={i} data-compare-row style={{ display: "grid", gridTemplateColumns: "minmax(160px, 1.4fr) repeat(4, minmax(110px, 1fr))", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
              <div data-compare-label style={{ padding: "20px 18px", fontSize: 14, color: "rgba(255,255,255,0.85)", lineHeight: 1.4 }}>{r.l}</div>
              <div className="cmp-cell" style={{ padding: "20px 18px", background: "rgba(196,130,58,0.10)" }}>{cell(r.eva)}</div>
              <div className="cmp-cell" style={{ padding: "20px 18px" }}>{cell(r.human)}</div>
              <div className="cmp-cell" style={{ padding: "20px 18px" }}>{cell(r.gpt)}</div>
              <div className="cmp-cell" style={{ padding: "20px 18px" }}>{cell(r.diy)}</div>
              <div data-compare-mobile-stack>
                {[
                  { label: "Eva", value: r.eva, hi: true },
                  { label: "Human assistant", value: r.human },
                  { label: "ChatGPT / Claude", value: r.gpt },
                  { label: "Do it yourself", value: r.diy },
                ].map((opt, j) => (
                  <div key={j} data-mob-row {...(opt.hi ? { "data-mob-row-eva": true } : {})}>
                    <span data-mob-row-label>{opt.label}</span>
                    <span data-mob-row-value>{cell(opt.value)}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
          <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 20, alignItems: "center", padding: "22px 22px", borderTop: "1px solid rgba(255,255,255,0.08)", background: "rgba(196,130,58,0.06)" }}>
            <div style={{ fontSize: 14, color: "rgba(255,255,255,0.85)", lineHeight: 1.45 }}>
              <span style={{ color: PALETTE.amberSoft, fontWeight: 600 }}>Already convinced?</span> Talk to an engineer — first workflows live this week.
            </div>
            <a href="#pricing" data-inline-cta style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              padding: "11px 20px", borderRadius: 999,
              background: "#fff", color: PALETTE.ink,
              textDecoration: "none", fontSize: 13, fontWeight: 600,
              whiteSpace: "nowrap",
            }}>
              See what she saves you <Icon name="arrowRight" size={13} />
            </a>
          </div>
        </div>
      </div>
      <style>{`
        [data-compare-mobile-stack] { display: none; }
        @media (max-width: 720px) {
          [data-compare-header] { display: none !important; }
          [data-compare-row] { display: block !important; padding: 16px 16px 14px !important; }
          [data-compare-label] { padding: 0 0 10px !important; font-size: 14px !important; font-weight: 600 !important; color: #fff !important; }
          .cmp-cell { display: none !important; }
          [data-compare-mobile-stack] { display: flex !important; flex-direction: column; gap: 6px; margin-top: 4px; }
          [data-mob-row] { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 9px 12px; border-radius: 10px; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06); }
          [data-mob-row-eva] { background: rgba(196,130,58,0.12); border-color: rgba(196,130,58,0.32); }
          [data-mob-row-label] { font-size: 12px; color: rgba(255,255,255,0.7); letter-spacing: 0.02em; }
          [data-mob-row-eva] [data-mob-row-label] { color: #F5DEB3; font-weight: 600; }
          [data-mob-row-value] { display: inline-flex; align-items: center; min-height: 20px; }
        }
      `}</style>
        <div style={{ marginTop: 28, textAlign: "center", color: "rgba(255,255,255,0.6)", fontSize: 13 }}>
          Still tempted to roll your own?<EvaVoiceTap clipId="guide_compare" hint="but couldn't i diy?" />
        </div>
    </section>
  );
};

/* Implementation replaces unverified proof on the rendered homepage.
   The legacy Proof component below is retained for evidence reconciliation. */
function Implementation() {
  const steps = [
    { title: "Scope the work", body: "Tell us which workflows and tools matter. We agree the requirements, approval rules, and rollout plan before implementation." },
    { title: "Implement and validate", body: "Your engineer connects Eva to the agreed tools and checks the workflow with your team before rollout." },
    { title: "Maintain and support", body: "We maintain your deployment, help your team use it, and scope further improvements when needed." },
  ];
  return (
    <section id="proof" style={{ padding: "clamp(44px, 10vw, 88px) clamp(20px, 5vw, 24px)", background: PALETTE.bg }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <SectionHead n="03" eyebrow="Implementation" title="A working deployment." italic="An engineering team behind it." lede="HireEva brings Eva into your business through an Electric Sheep engineering engagement." />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 20 }}>
          {steps.map((step) => (
            <div key={step.title} style={{ background: PALETTE.card, padding: 24, borderRadius: 16, border: `1px solid ${PALETTE.line}` }}>
              <h3 style={{ margin: "0 0 12px", fontSize: 18 }}>{step.title}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.6, color: PALETTE.ink2 }}>{step.body}</p>
            </div>
          ))}
        </div>
        <p style={{ marginTop: 28, fontSize: 15, lineHeight: 1.7, color: PALETTE.ink2 }}>
          Electric Sheep also builds business AI integrations and ecosystem engineering: plugins, MCP servers, CLI tools, and upstream maintenance. Start with a scope call, then a quote for a paid assessment or direct integration project.
        </p>
        <a href="https://www.electricsheephq.com/contact?intent=implementation" style={{ color: PALETTE.ink, fontWeight: 600, textDecoration: "underline" }}>Discuss broader engineering with Electric Sheep →</a>
      </div>
    </section>
  );
}

/* ---------- Legacy proof, not rendered ---------- */
function Proof() {
  type Source = "reddit" | "x" | "direct";
  type Quote = { q: string; n: string; r: string; handle: string; tag: string; source: Source; rating: 4 | 5; likes?: number };
  const QUOTES: Quote[] = [
    { q: "She saves me hours every day. I stopped doing quotes after dinner.", n: "David P.", r: "Owner · Trades", handle: "Verified owner", tag: "Trades", source: "direct", rating: 5 },
    { q: "I'm so glad to finally get my tech stack all moving in the same direction, and now it's cheaper than ever. She is solving all of my business ops issues.", n: "David D.", r: "Founder · Influencer Media Co", handle: "Verified owner", tag: "Media", source: "direct", rating: 5 },
    { q: "I was able to replace two of my staff. Eva does the dispatching now.", n: "Brett K.", r: "Owner · HVAC", handle: "Verified owner", tag: "HVAC", source: "direct", rating: 5 },
    { q: "Haven't missed a client message in 3 months. Home by 6 every night.", n: "Marcus W.", r: "Plumbing", handle: "u/marcusplumbs · r/smallbusiness", tag: "Plumbing", source: "reddit", rating: 5, likes: 412 },
    { q: "Eva chases invoices the way I never would. We stopped working Saturdays.", n: "Dr. Elena V.", r: "Dental practice owner", handle: "Verified owner", tag: "Dental", source: "direct", rating: 5 },
    { q: "Calendar's finally honest. Zero no-shows since she took over confirmations.", n: "Sarah C.", r: "Studio principal", handle: "@sarahc_studio", tag: "Studio", source: "x", rating: 4, likes: 218 },
    { q: "Filed Henderson 11 days early. Eva tracked the deadline I forgot existed.", n: "James O.", r: "Partner, law firm", handle: "u/jokafor · r/lawfirm", tag: "Legal", source: "reddit", rating: 5, likes: 287 },
    { q: "Eva runs renewals + churn outreach. NRR up 6 points, 12 hrs/week back.", n: "Aisha P.", r: "SaaS founder", handle: "@aishabuilds", tag: "SaaS", source: "x", rating: 5, likes: 534 },
    { q: "Two extra closings a month. She never forgets a buyer or a pre-approval.", n: "Ben C.", r: "Realty principal", handle: "u/realtor_bc · r/realestate", tag: "Realty", source: "reddit", rating: 4, likes: 196 },
    { q: "Customer DMs answered in seconds. Refund disputes down 40%.", n: "Priya S.", r: "E-comm owner", handle: "@priya_owns", tag: "E-comm", source: "x", rating: 5, likes: 421 },
    { q: "Tenant requests, vendor quotes, lease renewals — Eva handles it before I see it.", n: "Tom R.", r: "Property mgmt", handle: "u/tomrents · r/Entrepreneur", tag: "Property", source: "reddit", rating: 4, likes: 312 },
    { q: "She drafts session recaps in my voice. Clients think it's me. It's better than me.", n: "Maya L.", r: "Coach", handle: "Verified owner", tag: "Coaching", source: "direct", rating: 5 },
    { q: "Reservation confirms, supplier follow-ups, payroll reminders. One less manager.", n: "Carlos D.", r: "Restaurant owner", handle: "u/carloseats · r/restaurantowners", tag: "Restaurant", source: "reddit", rating: 4, likes: 178 },
  ];
  /* Stable amber-tinted gradient per name for monogram avatars */
  const avatarGradient = (n: string) => {
    const h = [...n].reduce((a, c) => a + c.charCodeAt(0), 0);
    const base = 18 + (h % 20);
    const mid = (base + 8) % 360;
    const deep = (base - 4 + 360) % 360;
    return `radial-gradient(circle at 30% 25%, hsl(${base}, 70%, 68%) 0%, hsl(${mid}, 60%, 52%) 45%, hsl(${deep}, 55%, 36%) 100%)`;
  };
  const initials = (n: string) => n.split(/\s+/).map(s => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
  const statsRef = useReveal<HTMLDivElement>();

  /* Carousel mechanics */
  const [slotsPerView, setSlotsPerView] = useState(3);
  const [page, setPage] = useState(0);
  const [paused, setPaused] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const compute = () => {
      const w = window.innerWidth;
      if (w < 640) setSlotsPerView(1);
      else if (w < 1024) setSlotsPerView(2);
      else setSlotsPerView(3);
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, []);
  const pageCount = Math.ceil(QUOTES.length / slotsPerView);
  useEffect(() => { setPage(p => Math.min(p, Math.max(0, pageCount - 1))); }, [pageCount]);
  useEffect(() => {
    if (paused || pageCount <= 1) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const id = setInterval(() => setPage(p => (p + 1) % pageCount), 8000);
    return () => clearInterval(id);
  }, [paused, pageCount]);
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowLeft") { e.preventDefault(); setPage(p => (p - 1 + pageCount) % pageCount); }
    if (e.key === "ArrowRight") { e.preventDefault(); setPage(p => (p + 1) % pageCount); }
  };

  const SourceBadge = ({ source }: { source: Source }) => {
    if (source === "reddit") return (
      <div title="Reddit" style={{ display: "inline-flex", alignItems: "center", gap: 5, color: "#FF4500", opacity: 0.7 }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden><path d="M22 12.07c0-1.21-.98-2.18-2.19-2.18-.59 0-1.13.23-1.52.62-1.5-1.07-3.56-1.77-5.85-1.85l1-4.7 3.27.69c.04.83.72 1.49 1.56 1.49.86 0 1.56-.7 1.56-1.56s-.7-1.56-1.56-1.56c-.62 0-1.15.36-1.4.89l-3.65-.78c-.1-.02-.2 0-.28.06-.08.05-.13.13-.15.23l-1.11 5.23c-2.32.06-4.41.76-5.93 1.86A2.18 2.18 0 0 0 4.19 9.9C2.98 9.9 2 10.86 2 12.07c0 .89.53 1.65 1.29 1.99-.03.21-.05.42-.05.64 0 3.27 3.81 5.93 8.51 5.93s8.51-2.66 8.51-5.93c0-.21-.02-.42-.05-.62.78-.34 1.32-1.11 1.32-2.01zM7 13.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5-.67 1.5-1.5 1.5S7 14.33 7 13.5zm8.71 4.05c-1 1-2.6 1.07-3.71 1.07s-2.71-.07-3.71-1.07a.4.4 0 0 1 0-.57.4.4 0 0 1 .57 0c.63.63 1.98.86 3.14.86s2.51-.23 3.14-.86a.4.4 0 0 1 .57.57zm-.21-2.55c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/></svg>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.06em" }}>Reddit</span>
      </div>
    );
    if (source === "x") return (
      <div title="X" style={{ display: "inline-flex", alignItems: "center", gap: 5, color: PALETTE.ink, opacity: 0.6 }}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.06em" }}>X</span>
      </div>
    );
    return (
      <div title="Verified owner" style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: PALETTE.amber, display: "inline-block" }} />
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", color: PALETTE.amber }}>Verified owner</span>
      </div>
    );
  };

  const cardWidthPct = 100 / slotsPerView;
  const translatePct = page * slotsPerView * cardWidthPct;

  return (
    <section id="proof" style={{ padding: "clamp(44px, 10vw, 88px) clamp(20px, 5vw, 24px)", background: PALETTE.bg }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <SectionHead n="03" eyebrow="The proof" title="People running businesses." italic="Not explaining them." lede="Real owners. Real revenue. Real time back. Not Reddit posts from people debugging their setup." />

        {/* P27.3 — NVIDIA authority pull-quote moved to TOP of Proof */}
        <div style={{ maxWidth: 760, margin: "0 auto 32px", padding: "32px 28px", background: PALETTE.bgAlt, border: `1px solid ${PALETTE.line}`, borderRadius: 20, textAlign: "center" }}>
          <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: PALETTE.amber, fontWeight: 700, marginBottom: 14 }}>
            The future is here
          </div>
          <p style={{ fontFamily: FONT_DISPLAY, fontStyle: "italic", fontSize: "clamp(20px, 2.4vw, 28px)", lineHeight: 1.35, color: PALETTE.ink, margin: 0, fontWeight: 400 }}>
            "Probably the single most important release of software, probably ever."
          </p>
          <div style={{ marginTop: 16, fontSize: 13, color: PALETTE.muted, fontFamily: FONT_MONO, letterSpacing: "0.04em" }}>
            — Jensen Huang, CEO of NVIDIA
          </div>
        </div>

        {/* Caption above carousel */}
        <div style={{ textAlign: "center", marginBottom: 18, fontFamily: FONT_MONO, fontSize: 12, letterSpacing: "0.12em", textTransform: "uppercase", color: PALETTE.amber, fontWeight: 700 }}>
          From the owners running it.
        </div>

        <div
          data-testimonial-carousel
          tabIndex={0}
          onKeyDown={onKey}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocus={() => setPaused(true)}
          onBlur={() => setPaused(false)}
          style={{ position: "relative", marginBottom: 28, outline: "none" }}
          aria-roledescription="carousel"
          aria-label="Owner testimonials"
        >
          <div style={{ overflow: "hidden" }}>
            <div
              ref={trackRef}
              style={{
                display: "flex",
                transform: `translateX(-${translatePct}%)`,
                transition: "transform 480ms cubic-bezier(.2,.8,.2,1)",
              }}
            >
              {QUOTES.map((q, i) => (
                <div key={i} style={{ flex: `0 0 ${cardWidthPct}%`, padding: "0 8px", boxSizing: "border-box" }}>
                  <div style={{ background: PALETTE.card, borderRadius: 20, padding: 24, border: `1px solid ${PALETTE.line}`, position: "relative", display: "flex", flexDirection: "column", height: "100%", minHeight: 280 }}>
                    <div style={{ position: "absolute", top: 14, right: 16 }}>
                      <SourceBadge source={q.source} />
                    </div>
                    <div aria-label={`${q.rating} out of 5 stars`} style={{ display: "flex", gap: 2, marginBottom: 12, fontSize: 13, lineHeight: 1 }}>
                      {[0,1,2,3,4].map(s => (
                        <span key={s} aria-hidden style={{ color: s < q.rating ? PALETTE.amber : "rgba(196,130,58,0.25)" }}>★</span>
                      ))}
                    </div>
                    <p style={{ fontFamily: FONT_DISPLAY, fontStyle: "italic", fontSize: 17, lineHeight: 1.4, color: PALETTE.ink, margin: 0, fontWeight: 400, flex: 1 }}>"{q.q}"</p>
                    <div style={{ marginTop: 18, paddingTop: 14, borderTop: `1px solid ${PALETTE.line}`, display: "flex", alignItems: "center", gap: 12 }}>
                      <div
                        aria-hidden
                        style={{
                          width: 48, height: 48, borderRadius: "50%",
                          background: avatarGradient(q.n),
                          display: "grid", placeItems: "center",
                          color: "#fff",
                          fontFamily: FONT_DISPLAY, fontStyle: "italic",
                          fontSize: 18, fontWeight: 500,
                          flexShrink: 0,
                          border: "1.5px solid rgba(196,130,58,0.35)",
                          boxShadow: "inset 0 0 0 2px rgba(196,130,58,0.25), 0 2px 6px rgba(0,0,0,0.10)",
                        }}
                      >
                        {initials(q.n)}
                      </div>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: PALETTE.ink }}>{q.n}</div>
                        <div style={{ fontSize: 11.5, color: PALETTE.muted, marginTop: 2 }}>{q.r}</div>
                        <div style={{ fontSize: 10.5, color: PALETTE.muted, opacity: 0.75, marginTop: 1, fontFamily: FONT_MONO, letterSpacing: "0.02em" }}>
                          {q.handle}{q.likes ? ` · ▲ ${q.likes}` : ""}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {pageCount > 1 && (
            <>
              <button
                onClick={() => setPage(p => (p - 1 + pageCount) % pageCount)}
                aria-label="Previous testimonials"
                style={{
                  position: "absolute", left: -8, top: "50%", transform: "translateY(-50%)",
                  width: 44, height: 44, borderRadius: "50%",
                  background: "rgba(255,255,255,0.92)", border: `1px solid ${PALETTE.line}`,
                  cursor: "pointer", display: "grid", placeItems: "center",
                  boxShadow: "0 2px 10px rgba(0,0,0,0.08)", color: PALETTE.ink,
                  zIndex: 2,
                }}
              >
                <span aria-hidden style={{ display: "inline-flex", transform: "rotate(90deg)" }}>
                  <Icon name="chevDown" size={18} />
                </span>
              </button>
              <button
                onClick={() => setPage(p => (p + 1) % pageCount)}
                aria-label="Next testimonials"
                style={{
                  position: "absolute", right: -8, top: "50%", transform: "translateY(-50%)",
                  width: 44, height: 44, borderRadius: "50%",
                  background: "rgba(255,255,255,0.92)", border: `1px solid ${PALETTE.line}`,
                  cursor: "pointer", display: "grid", placeItems: "center",
                  boxShadow: "0 2px 10px rgba(0,0,0,0.08)", color: PALETTE.ink,
                  zIndex: 2,
                }}
              >
                <span aria-hidden style={{ display: "inline-flex", transform: "rotate(-90deg)" }}>
                  <Icon name="chevDown" size={18} />
                </span>
              </button>
            </>
          )}
        </div>

        {pageCount > 1 && (
          <div style={{ display: "flex", justifyContent: "center", gap: 8, marginBottom: 48, flexWrap: "wrap" }}>
            {Array.from({ length: pageCount }).map((_, i) => (
              <button
                key={i}
                onClick={() => setPage(i)}
                aria-label={`Go to page ${i + 1}`}
                style={{
                  width: i === page ? 24 : 8, height: 8, borderRadius: 999,
                  background: i === page ? PALETTE.amber : "rgba(0,0,0,0.18)",
                  border: 0, cursor: "pointer", padding: 0,
                  transition: "width 240ms ease, background 240ms ease",
                }}
              />
            ))}
          </div>
        )}

        <div ref={statsRef} data-reveal-stagger style={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: "clamp(48px, 8vw, 96px)", paddingTop: 40, marginTop: 12, borderTop: `1px solid ${PALETTE.line}`, textAlign: "center" }}>
          {[
            { v: "11 hrs", l: "Avg. time back per week" },
            { v: "98%", l: "On-time invoicing" },
            { v: "< 24h", l: "Avg. setup time" },
          ].map((s, i) => (
            <div key={i}>
              <div style={{ fontFamily: FONT_DISPLAY, fontStyle: "italic", fontSize: "clamp(40px, 4.5vw, 60px)", color: PALETTE.amber, lineHeight: 1, fontWeight: 400 }}>{s.v}</div>
              <div style={{ marginTop: 8, fontSize: 13, color: PALETTE.muted }}>{s.l}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- Pricing ---------- */
type PricingCycle = "monthly" | "quarterly" | "semiannual";

/**
 * RotatingPhrase — cycles through a list of grammatically-paired phrases
 * so different industries see themselves in the headline. Crossfade only,
 * no layout shift (uses inline-block min-width). Pauses on reduced-motion.
 */
const ROTATING_PHRASES = [
  "admin work",
  "inbox time",
  "paperwork",
  "quote chasing",
  "weekend hours",
] as const;
function RotatingPhrase() {
  const hostRef = useRef<HTMLSpanElement>(null);
  const i = useRotator(ROTATING_PHRASES.length, 3800, hostRef);
  return (
    <span
      ref={hostRef}
      style={{
        display: "inline-grid",
        gridAutoColumns: "max-content",
        justifyItems: "center",
        verticalAlign: "baseline",
      }}
    >
      {ROTATING_PHRASES.map((phrase, n) => (
        <span
          key={phrase}
          aria-hidden={n !== i}
          style={{
            gridArea: "1 / 1",
            whiteSpace: "nowrap",
            textDecoration: "underline wavy rgba(196,130,58,0.55)",
            textDecorationThickness: "2px",
            textUnderlineOffset: "8px",
            opacity: n === i ? 1 : 0,
            transform: n === i ? "translateY(0)" : "translateY(-8px)",
            transition: "opacity 360ms ease-out, transform 360ms ease-out",
            animation: n === i ? "rotator-swipe-in 360ms ease-out both" : undefined,
          }}
        >
          {phrase}
        </span>
      ))}
      <style>{ROTATOR_SWIPE_CSS}</style>
    </span>
  );
}

function Pricing() {
  // Capture ?ref=CODE on the homepage (mirrors /r/:code) so a direct link
  // like hireeva.co/?ref=DAVID-POKU-XA still attributes the Insider.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (!ref) return;
    const upper = ref.toUpperCase();
    try {
      localStorage.setItem("eva_ref", upper);
      localStorage.setItem("eva_ref_exp", String(Date.now() + 90 * 24 * 60 * 60 * 1000));
    } catch { /* ignore */ }
  }, []);
  const gridRef = useReveal<HTMLDivElement>();
  const INCLUDED = [
    "**An assigned engineer embedded with your team**",
    "Eva on WhatsApp, SMS, email, Slack & voice",
    "Company brain — memory across email, calendar, docs & invoices",
    "Integrations with your CRM, invoicing & calendar",
    "Owner approval gates on every risky action",
    "Hosting, security & staff training",
  ];
  return (
    <section id="pricing" style={{ padding: "clamp(32px, 6vw, 56px) clamp(20px, 5vw, 24px)", background: PALETTE.bgAlt }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <SectionHead n="04" eyebrow="Pricing · one engagement" title="An engineer, Eva," italic="and ongoing support." lede="Start with a scope call to agree what we build and how we support it." />
        <div ref={gridRef} data-reveal data-pricing-card style={{
          marginTop: 32, background: PALETTE.ink, color: "#fff", borderRadius: 24,
          padding: "clamp(24px, 5vw, 48px)", display: "grid", gap: 32,
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
        }}>
          <div>
            <div style={{ fontFamily: FONT_MONO, fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: PALETTE.amberSoft, fontWeight: 700 }}>
              Your engineer · scoped implementation
            </div>
            <div data-price-block style={{ display: "flex", alignItems: "baseline", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
              <span style={{ fontSize: 15, color: "rgba(255,255,255,0.6)" }}>From</span>
              <span style={{ fontFamily: FONT_DISPLAY, fontStyle: "italic", fontSize: "clamp(56px, 7vw, 72px)", lineHeight: 1 }}>{OFFER.startingPrice}</span>
              <span style={{ fontSize: 16, color: "rgba(255,255,255,0.6)" }}>/ month</span>
            </div>
            <p style={{ fontSize: 15, color: "rgba(255,255,255,0.78)", lineHeight: 1.55, margin: "16px 0 24px", maxWidth: 440 }}>
              Scoped to your size and what we build. An engineer deploys Eva and your AI workflows into your business — then it keeps running.
            </p>
            <ul style={{ listStyle: "none", padding: 0, margin: "0 0 28px", display: "grid", gap: 10 }}>
              {INCLUDED.map((f) => {
                const bold = f.startsWith("**");
                const txt = f.replace(/\*\*/g, "");
                return (
                  <li key={f} style={{ display: "flex", gap: 10, fontSize: 14.5, color: "rgba(255,255,255,0.88)", fontWeight: bold ? 600 : 400 }}>
                    <span style={{ color: PALETTE.amberSoft }}><Icon name="check" size={14} /></span>{txt}
                  </li>
                );
              })}
            </ul>
            <button type="button" data-pricing-cta="amber" onClick={() => openCallMeNow("pricing_engagement")} style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "14px 26px",
              background: PALETTE.amber, color: "#fff", borderRadius: 999, border: 0, fontSize: 14, fontWeight: 600,
              minHeight: 48, cursor: "pointer", fontFamily: "inherit",
            }}>
              <Icon name="phone" size={14} /> Talk to an engineer
            </button>
          </div>
          <ol style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 14, alignContent: "center" }}>
            {[
              { t: "Scope", b: "Map your workflows, access requirements and priorities with your engineer." },
              { t: "Implement", b: "Build the agreed integrations and validate them with your team." },
              { t: "Train and expand", b: "Help your team use the deployment and scope further improvements." },
              { t: "Maintain", b: "Keep the agreed deployment supported. Timing is set for your scope." },
            ].map((p, i) => (
              <li key={p.t} style={{ display: "flex", gap: 14, padding: 16, borderRadius: 14, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)" }}>
                <span style={{ fontFamily: FONT_MONO, fontSize: 12, color: PALETTE.amberSoft, fontWeight: 700, minWidth: 22 }}>0{i + 1}</span>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600 }}>{p.t}</div>
                  <div style={{ fontSize: 13.5, color: "rgba(255,255,255,0.65)", marginTop: 4, lineHeight: 1.45 }}>{p.b}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>
        <div style={{ marginTop: 18, textAlign: "center", fontSize: 12.5, color: PALETTE.muted }}>
          Whole company?{" "}
          <Link to="/cobrain" style={{ color: PALETTE.ink2, textDecoration: "underline", textUnderlineOffset: 3, fontWeight: 600 }}>See the company brain →</Link>
          {" · "}
          <Link to="/services" style={{ color: PALETTE.ink2, textDecoration: "underline", textUnderlineOffset: 3, fontWeight: 600 }}>How a deployment works →</Link>
        </div>
      </div>
    </section>
  );
};

/* ---------- FAQ ---------- */
function FAQ() {
  const items: Array<{ q: string; a: string; voiceClipId?: string; hint?: string }> = [
    { q: "How is Eva different from a general AI chat tool?", a: "HireEva includes an engineer who implements connected workflows around Eva, with agreed approval rules and ongoing support. The scope can include client replies, scheduling, invoicing, and other work across your tools." },
    { q: "What does it cost?", a: "Engagements start at $1,500/mo, scoped to your size and what we build. You get a clear number on the first call — no surprises." },
    { q: "How fast will I see results?", a: "We agree rollout timing after scoping your workflows, integrations, and access requirements. Your engineer validates the first workflow with your team and supports it after rollout." },
    { q: "Is there a minimum commitment?", a: "Yes, a 3-month initial term for implementation and support. After that it runs month to month." },
    { q: "What happens after the first 3 months?", a: "Your AI-enhanced business keeps running. We keep it hosted, maintained and upgraded as AI improves — and your engineer scopes new projects whenever you want them." },
    { q: "Do you only use your own software?", a: "Eva and evaOS are our core — and what we deploy most. Where another tool fits a job better (Claude, Codex, custom code, or simply training your team), we use it. The job is solving the problem." },
    { q: "Is your stack proprietary or built on something?", a: "Eva and evaOS combine our software and integrations with open-source agent runtimes, including Hermes and OpenClaw. Your engineer configures and maintains the deployment for the agreed scope." },
    { q: "Which tools can Eva connect to?", a: "We scope the tools and API access your workflows need. Your engineer confirms integration support and any custom work before implementation." },
    { q: "Will she send things without my approval?", a: "Only if you tell her to. By default everything goes through a 'draft → approve → send' loop. As you build trust, you can turn approval off for specific actions or clients." },
    { q: "What if she gets something wrong?", a: "AI can make mistakes. Use the agreed approval rules for important actions, and work with your engineer to correct the workflow and validate the change." },
    { q: "Where is my data processed?", a: "Eva runs on dedicated hosting. Requests can also be processed by AI model providers and connected services. Your engineer scopes the integrations, access, and data flow with you; provider processing is subject to the applicable provider terms." },
    { q: "Can I cancel?", a: "Yes. After the 3-month initial term, it's month to month and you can cancel with 30 days' notice. You keep all your data — exports included." },
  ];
  return (
    <section id="faq" style={{ padding: "clamp(44px, 10vw, 88px) clamp(20px, 5vw, 24px)", background: PALETTE.bg }}>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: items.map((it) => ({
            "@type": "Question",
            name: it.q,
            acceptedAnswer: { "@type": "Answer", text: it.a },
          })),
        })}</script>
      </Helmet>
      <div data-faq-accordion style={{ maxWidth: 800, margin: "0 auto" }}>
        <SectionHead n="05" eyebrow="FAQ" title="The honest" italic="answers." />
        <div style={{ display: "flex", flexDirection: "column" }}>
          {items.map((it, i) => <FaqItem key={i} {...it} />)}
        </div>
      </div>
    </section>
  );
};
const FaqItem = ({ q, a, voiceClipId, hint }: { q: string; a: string; voiceClipId?: string; hint?: string }) => {
  const [open, setOpen] = useState(false);
  const innerRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useLayoutEffect(() => {
    if (!innerRef.current) return;
    const measure = () => setHeight(innerRef.current?.scrollHeight ?? 0);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(innerRef.current);
    return () => ro.disconnect();
  }, [a]);

  return (
    <div style={{ borderTop: `1px solid ${PALETTE.line}` }}>
      <button
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        style={{ width: "100%", padding: "24px 0", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, background: "transparent", border: 0, cursor: "pointer", textAlign: "left", position: "relative", zIndex: 41 }}
      >
        <span style={{ fontSize: 18, color: PALETTE.ink, fontFamily: FONT_BODY, fontWeight: 500, flex: 1, minWidth: 0 }}>{q}</span>
        <span style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 240ms cubic-bezier(.2,.8,.2,1)", color: PALETTE.muted, display: "inline-flex" }}>
          <Icon name="chevDown" size={18} />
        </span>
      </button>
      <div
        style={{
          height: open ? height : 0,
          overflow: "hidden",
          transition: "height 320ms cubic-bezier(.2,.8,.2,1)",
        }}
      >
        <div ref={innerRef} style={{ paddingBottom: 24 }}>
          <p style={{ fontSize: 15, color: PALETTE.ink2, lineHeight: 1.6, margin: 0, maxWidth: 680 }}>
            {a}
          </p>
          {voiceClipId && hint ? (
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
              <span style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: PALETTE.muted, fontWeight: 600 }}>
                Hear Eva's answer
              </span>
              <EvaVoiceTap clipId={voiceClipId} hint={hint} />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

/* ---------- CTA + Footer ---------- */
const CtaHlsVideo = ({ src }: { src: string }) => {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    let hls: import("hls.js").default | null = null;
    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = src;
    } else {
      let cancelled = false;
      import("hls.js").then(({ default: Hls }) => {
        if (cancelled || !ref.current) return;
        if (Hls.isSupported()) {
          hls = new Hls({ enableWorker: true, lowLatencyMode: false });
          hls.loadSource(src);
          hls.attachMedia(ref.current);
        } else {
          ref.current.src = src;
        }
      });
      return () => { cancelled = true; if (hls) hls.destroy(); };
    }
    return () => { if (hls) hls.destroy(); };
  }, [src]);
  return (
    <video
      ref={ref}
      aria-hidden="true"
      autoPlay
      muted
      loop
      playsInline
      preload="metadata"
      style={{
        position: "absolute", inset: 0, width: "100%", height: "100%",
        objectFit: "cover", zIndex: 0, opacity: 0.62,
        mixBlendMode: "luminosity", pointerEvents: "none",
      }}
    />
  );
};

const CTA = () => {
  const showCtaVideo = useMediaQuery("(min-width: 641px) and (prefers-reduced-motion: no-preference)");
  return (
  <section id="cta-final" data-cta-final style={{ padding: "120px 24px 96px", background: PALETTE.bgAlt, textAlign: "center", position: "relative", overflow: "hidden" }}>
    {showCtaVideo && (
      <>
        <video
          aria-hidden="true"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          src="https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260402_054547_9875cfc5-155a-4229-8ec8-b7ba7125cbf8.mp4"
          style={{
            position: "absolute", inset: 0, width: "100%", height: "100%",
            objectFit: "cover", zIndex: 0, opacity: 0.40,
            mixBlendMode: "luminosity", pointerEvents: "none",
          }}
        />
        <div aria-hidden style={{
          position: "absolute", inset: 0, zIndex: 0, pointerEvents: "none",
          background: "linear-gradient(180deg, rgba(244,239,228,0.6) 0%, rgba(244,239,228,0.4) 40%, rgba(244,239,228,0.78) 100%)",
        }} />
      </>
    )}
    <div aria-hidden style={{
      position: "absolute", inset: "-20% -10%",
      background: `radial-gradient(circle at center, ${PALETTE.amberSoft}22 0%, transparent 55%)`,
      filter: "blur(60px)", pointerEvents: "none", zIndex: 0,
    }} />
    <div style={{ maxWidth: 900, margin: "0 auto", position: "relative", zIndex: 1 }}>
      <h2 data-cta-h2 style={{ fontFamily: FONT_DISPLAY, fontStyle: "italic", fontSize: "clamp(48px, 8vw, 104px)", lineHeight: 1, color: PALETTE.ink, margin: 0, fontWeight: 400, letterSpacing: "-0.02em" }}>
        Put AI to work in your business.
      </h2>
      <p data-cta-lede style={{ marginTop: 32, fontSize: 18, color: PALETTE.ink2, lineHeight: 1.5, maxWidth: 560, margin: "32px auto 0" }}>
        Start with the workflows you want to improve. An Electric Sheep engineer will help scope your Eva implementation and ongoing support.
      </p>
      <div data-inline-cta data-cta-buttons style={{ display: "flex", gap: 12, justifyContent: "center", marginTop: 36, flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={() => openCallMeNow("cta")}
          style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "14px 26px", background: PALETTE.ink, color: "#fff", borderRadius: 999, border: 0, cursor: "pointer", fontSize: 14, fontWeight: 500, fontFamily: "inherit", minHeight: 48 }}
        >
          Talk through your scope <Icon name="arrowRight" size={15} />
        </button>
        <a href="#pricing" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "14px 26px", background: "transparent", border: `1px solid ${PALETTE.ink}`, color: PALETTE.ink, borderRadius: 999, textDecoration: "none", fontSize: 14, fontWeight: 500, minHeight: 48 }}>
          See how it works
        </a>
      </div>
      <div data-cta-trust style={{ display: "flex", alignItems: "center", gap: "8px 14px", marginTop: 24, fontSize: 12.5, color: PALETTE.muted, flexWrap: "wrap", justifyContent: "center" }}>
        {[
          OFFER.priceLine,
          "Your own engineer",
          "Scoped implementation",
          "Ongoing support",
        ].map((label, i) => (
          <span key={label} style={{ display: "inline-flex", alignItems: "center", gap: 14, whiteSpace: "nowrap" }}>
            {i > 0 && <span aria-hidden style={{ width: 4, height: 4, borderRadius: "50%", background: PALETTE.amber }} />}
            <span>{label}</span>
          </span>
        ))}
      </div>
    </div>
  </section>
  );
};

function Footer() {
  return <SiteFooter product="software" variant="rich" callSource="footer_home" />;
}

/* ---------- Page ---------- */
function Index() {
  useEffect(() => { document.title = "HireEva — engineer-led AI implementation by Electric Sheep"; }, []);
  return (
    <div style={{ background: PALETTE.bg, color: PALETTE.ink, fontFamily: FONT_BODY, minHeight: "100vh" }}>
      <Helmet>
        <link rel="canonical" href="https://www.hireeva.io/" />
        <meta property="og:url" content="https://www.hireeva.io/" />
      </Helmet>
      {/* P13.1 + P13.7 — Document-level overflow clip + mobile section padding scale */}
      <style>{`
        html, body { overflow-x: clip; max-width: 100vw; }
        @supports not (overflow: clip) { html, body { overflow-x: hidden; } }
        /* L24 · P24.4 — anchor scroll lands below sticky nav */
        #compare, #proof, #pricing, #capability, #faq { scroll-margin-top: 80px; }
        /* L24 · P24.4 H — tighten phone→ticker gap on tablet too (was mobile-only at ≤640) */
        @media (min-width: 641px) and (max-width: 880px) {
          .hero-phone-wrap { margin-bottom: -16px !important; }
          .hero-ticker-below-phone { margin-top: 0 !important; }
        }
        @media (max-width: 640px) {
          main > section, body section[id] {
            padding-left: 20px !important;
            padding-right: 20px !important;
          }
          /* L20 · P20.1 — Footer mobile centering & logo lockup */
          [data-footer] { padding: 32px 20px 40px !important; }
          [data-footer-bottom] { flex-direction: column !important; align-items: center !important; justify-content: center !important; gap: 18px !important; text-align: center !important; }
          [data-footer-brand] { flex-direction: column !important; align-items: center !important; justify-content: center !important; gap: 14px !important; text-align: center !important; }
          [data-footer-brand] > span { text-align: center !important; }
          [data-footer-status] { justify-content: center !important; }
          [data-footer-legal] { justify-content: center !important; width: 100% !important; }
          /* Pricing cards on mobile — left-aligned for legibility (checkmarks form a single column) */
          [data-pricing-card] { text-align: left !important; align-items: stretch !important; }
          [data-pricing-card] ul { align-items: stretch !important; }
          [data-pricing-card] ul li { justify-content: flex-start !important; text-align: left !important; }
          [data-pricing-card] [data-price-block] { text-align: left !important; }
          [data-pricing-card] button[data-pricing-cta], [data-pricing-card] a[data-pricing-cta] { width: 100% !important; }
          [data-pricing-tag] { left: 16px !important; top: -10px !important; }
          /* L20 · P20.3 — FAQ centered text on mobile */
          #faq button { text-align: center !important; justify-content: center !important; }
          #faq button + div p, #faq [data-faq-answer] { text-align: center !important; margin-left: auto !important; margin-right: auto !important; }
          /* L20 · P20.4 — Compare mobile pill cluster centered */
          [data-compare-mobile-stack] { justify-content: center !important; }
          [data-compare-label] { text-align: center !important; }
          /* L21 · P21.2 — Final CTA mobile polish */
          [data-cta-h2] { font-size: clamp(40px, 11vw, 56px) !important; }
          [data-cta-lede] { font-size: 16.5px !important; line-height: 1.55 !important; }
          [data-cta-buttons] { flex-direction: column !important; gap: 10px !important; width: 100% !important; }
          [data-cta-buttons] > a { width: 100% !important; }
          [data-cta-trust] { gap: 10px !important; }
          /* L21 · P21.3 — Mobile micro-polish */
          .hero-grid > div:first-child > p { font-size: 16.5px !important; line-height: 1.55 !important; }
          [data-cap-head] { gap: 10px !important; }
          [data-compare-row] { border-color: rgba(0,0,0,0.06) !important; }
        }
      `}</style>
      <Nav />
      <main id="page-main">
      <Hero />
      <Capability />
      {/* P10.7 — IndustryStrip lives between Capability and the next CTA · P27.9 spacing tightened */}
      {/* Industry counts are omitted until backed by publishable evidence. */}
      <InlineCTA href="#proof" eyebrow="Up next" line="See how an Electric Sheep engineer scopes and supports your deployment." cta="Implementation →" />
      <Suspense fallback={<div style={{ minHeight: 480 }} />}>
        <VoiceDemo />
      </Suspense>
      <Implementation />
      <Pricing />
      <FAQ />
      {/* P31.11 — Stack band lives under the FAQ as supporting authority */}
      <StackBand />
      <CTA />
      </main>
      <Footer />
      {/* Unverified proof and savings prompts are not rendered on this homepage. */}
    </div>
  );
};
export default Index;
